import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiBriefcase,
  FiClock,
  FiColumns,
  FiDownload,
  FiEdit2,
  FiEye,
  FiGitMerge,
  FiInfo,
  FiList,
  FiMail,
  FiPhone,
  FiPlus,
  FiSearch,
  FiTarget,
  FiTrash2,
  FiUpload,
  FiUser,
  FiUserPlus,
  FiX,
} from "react-icons/fi";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  assignCrmLead,
  createCrmCompany,
  createCrmContact,
  createCrmLead,
  fetchCrmAssignableUsers,
  fetchCrmCompanies,
  fetchCrmContacts,
  fetchCrmLead,
  fetchCrmLeadAssignmentHistory,
  fetchCrmLeads,
  fetchCrmOpportunityPipelines,
  importCrmLeads,
  mergeCrmLeads,
  unmergeCrmLead,
  updateCrmContact,
  updateCrmLead,
} from "../crmApi";
import CrmMergeModal from "../components/CrmMergeModal";
import CrmMergeHistory from "../components/CrmMergeHistory";
import { useCrmCopy } from "../crmI18n";
import ZadarmaCallButton from "../telephony/ZadarmaCallButton";
import WhatsAppStartButton from "../whatsapp/WhatsAppStartButton";

const emptyForm = {
  title: "",
  stage: "new",
  status: "open",
  contact_customer_id: "",
  company_id: "",
  estimated_value: "",
  expected_close_date: "",
  lead_pipeline_id: "",
  lead_pipeline_stage_id: "",
  opportunity_pipeline_id: "",
  opportunity_stage_id: "",
  source: "",
  notes: "",
};

const CREATE_CONTACT_VALUE = "__create_contact__";
const CREATE_COMPANY_VALUE = "__create_company__";
const ALL_PIPELINES_FILTER = "__all_pipelines__";
const NO_PIPELINE_FILTER = "__no_pipeline__";
const LEADS_PAGE_SIZE = 100;
const LEADS_EXPORT_PAGE_SIZE = 200;

const emptyContactForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_id: "",
  job_title: "",
  department: "",
  crm_status: "active",
  source: "",
  notes: "",
};

const emptyCompanyForm = {
  name: "",
  legal_name: "",
  tax_id: "",
  website: "",
  email: "",
  phone: "",
  industry: "",
  company_type: "",
  status: "active",
  address: "",
  city: "",
  country: "",
  postal_code: "",
  notes: "",
};

const CRM_IMPORT_TEMPLATE_COLUMNS = [
  ...[
    ["name", "conditional", "Required to create a new company. Reused by exact name, tax ID or email.", "Blue Bay Hotels"],
    ["legal_name", "optional", "Registered company name.", "Blue Bay Hotels SL"],
    ["tax_id", "optional", "Used to reuse an existing company when possible.", "B12345678"],
    ["website", "optional", "Company website.", "https://bluebay.example"],
    ["email", "optional", "Company email. Used to reuse an existing company when possible.", "sales@bluebay.example"],
    ["phone", "optional", "Company phone.", "+34 600 000 000"],
    ["address", "optional", "Company address.", "Main street 1"],
    ["city", "optional", "Company city.", "Madrid"],
    ["country", "optional", "Company country.", "Spain"],
    ["postal_code", "optional", "Company postal code.", "28001"],
    ["industry", "optional", "Industry or market segment.", "Hospitality"],
    ["company_type", "optional", "Company type.", "Hotel group"],
    ["lifecycle_stage", "optional", "Defaults to customer.", "customer"],
    ["status", "optional", "Defaults to active.", "active"],
    ["owner_user_id", "optional", "Existing user ID responsible for the company.", "1"],
    ["notes", "optional", "Company notes.", "Key account"],
  ].map(([field, requirement, notes, example]) => ({
    key: `company.${field}`,
    group: "Company",
    requirement,
    notes,
    example,
  })),
  ...[
    ["salutation", "optional", "Allowed: Mr, Mrs, Ms, Mx, None.", "Ms"],
    ["first_name", "conditional", "At least first name, last name, email or phone is required to create a contact.", "Maria"],
    ["middle_name", "optional", "Middle name.", ""],
    ["last_name", "conditional", "At least first name, last name, email or phone is required to create a contact.", "Garcia"],
    ["second_last_name", "optional", "Second last name.", "Lopez"],
    ["gender", "optional", "Allowed: male, female, other, undisclosed.", "undisclosed"],
    ["birth_date", "optional", "Use YYYY-MM-DD.", "1988-05-21"],
    ["email", "conditional", "At least first name, last name, email or phone is required. Reused when possible.", "maria@bluebay.example"],
    ["phone", "conditional", "At least first name, last name, email or phone is required. Reused when possible.", "+34 611 111 111"],
    ["country", "optional", "Contact country.", "Spain"],
    ["city", "optional", "Contact city.", "Madrid"],
    ["address", "optional", "Contact address.", "Main street 1"],
    ["postal_code", "optional", "Contact postal code.", "28001"],
    ["document_type", "optional", "Allowed: passport, id, driving_license.", "id"],
    ["document_number", "optional", "Used to reuse an existing contact when possible.", "12345678Z"],
    ["is_vip", "optional", "Use true/false, yes/no or 1/0.", "false"],
    ["vip_level", "optional", "VIP tier.", "gold"],
    ["photo_url", "optional", "Contact photo URL.", ""],
    ["customer_description", "optional", "Customer notes from the PMS profile.", "Frequent corporate booker"],
    ["customer_status", "optional", "Allowed: active, inactive. Defaults to active.", "active"],
    ["company_id", "optional", "Existing company ID for the contact. Leave blank when using company.* columns.", ""],
    ["job_title", "optional", "CRM job title.", "Revenue Manager"],
    ["department", "optional", "CRM department.", "Sales"],
    ["lifecycle_stage", "optional", "Defaults to contact.", "contact"],
    ["crm_status", "optional", "CRM profile status. Defaults to active.", "active"],
    ["source", "optional", "Contact source.", "Trade fair"],
    ["owner_user_id", "optional", "Existing user ID responsible for the contact.", "1"],
    ["notes", "optional", "CRM contact notes.", "Interested in Q4 campaign"],
    ["last_contacted_at", "optional", "Use YYYY-MM-DD or a valid date-time.", "2026-06-08"],
  ].map(([field, requirement, notes, example]) => ({
    key: `contact.${field}`,
    group: "Contact",
    requirement,
    notes,
    example,
  })),
  ...[
    ["id", "optional", "Existing lead ID. When present, CRM updates that lead instead of matching by title/contact/company.", ""],
    ["contact_customer_id", "optional", "Existing contact/customer ID. Leave blank when using contact.* columns.", ""],
    ["company_id", "optional", "Existing company ID. Leave blank when using company.* columns.", ""],
    ["title", "optional", "If blank, CRM generates it from company, contact or source.", "Corporate group inquiry Q4"],
    ["stage", "optional", "Allowed: new, qualified, proposal, won, lost. Defaults to new.", "new"],
    ["status", "optional", "Defaults from stage: open, won or lost.", "open"],
    ["source", "optional", "Lead source.", "Website"],
    ["estimated_value", "optional", "Numeric value.", "12500.00"],
    ["expected_close_date", "optional", "Use YYYY-MM-DD.", "2026-09-30"],
    ["owner_user_id", "optional", "Existing user ID responsible for the lead.", "1"],
    ["current_owner_user_id", "optional", "Read-only export field. Ignored on import.", "1"],
    ["current_owner_name", "optional", "Read-only export field. Ignored on import.", "Alex Manager"],
    ["current_owner_email", "optional", "Read-only export field. Ignored on import.", "alex@example.com"],
    ["new_owner_user_id", "optional", "Set this only when you want to reassign the lead. Blank keeps the current owner.", "2"],
    ["notes", "optional", "Lead notes.", "Needs proposal before July"],
  ].map(([field, requirement, notes, example]) => ({
    key: `lead.${field}`,
    group: "Lead",
    requirement,
    notes,
    example,
  })),
];

const CRM_IMPORT_COLUMN_KEYS = new Set(CRM_IMPORT_TEMPLATE_COLUMNS.map((column) => column.key));
const CRM_IMPORT_ACCEPT = ".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const CRM_IMPORT_PREVIEW_ROW_LIMIT = 10;
const CRM_IMPORT_PREVIEW_ERROR_LIMIT = 12;
const CRM_LEADS_BULK_EXPORT_COLUMNS = [
  "lead.id",
  "lead.title",
  "lead.stage",
  "lead.status",
  "lead.source",
  "lead.estimated_value",
  "lead.expected_close_date",
  "lead.contact_customer_id",
  "lead.company_id",
  "lead.current_owner_user_id",
  "lead.current_owner_name",
  "lead.current_owner_email",
  "lead.new_owner_user_id",
  "lead.notes",
  "contact.first_name",
  "contact.middle_name",
  "contact.last_name",
  "contact.second_last_name",
  "contact.email",
  "contact.phone",
  "contact.country",
  "contact.city",
  "contact.document_number",
  "contact.customer_status",
  "company.name",
  "company.legal_name",
  "company.tax_id",
  "company.email",
  "company.phone",
  "company.city",
  "company.country",
  "company.status",
];

function buildFormFromLead(lead) {
  return {
    title: lead?.title || "",
    stage: lead?.stage || "new",
    status: lead?.status || getDefaultLeadStatusForStage(lead?.stage || "new"),
    contact_customer_id: lead?.contact_customer_id || "",
    company_id: lead?.company_id || "",
    estimated_value: lead?.estimated_value ?? "",
    expected_close_date: lead?.expected_close_date || "",
    lead_pipeline_id: lead?.lead_pipeline_id || "",
    lead_pipeline_stage_id: lead?.lead_pipeline_stage_id || "",
    opportunity_pipeline_id: lead?.opportunity_pipeline_id || "",
    opportunity_stage_id: lead?.opportunity_stage_id || "",
    source: lead?.source || "",
    notes: lead?.notes || "",
  };
}

function getDefaultLeadStatusForStage(stage) {
  if (stage === "won") return "won";
  if (stage === "lost") return "lost";
  return "open";
}

function getLeadStageDisplay(lead, copy, pageCopy) {
  if (lead?.status === "inactive") {
    return {
      key: "inactive",
      label: pageCopy.statuses?.inactive || copy.common.inactive || "Inactive",
    };
  }

  const stage = lead?.stage || "new";
  return {
    key: stage,
    label: copy.leadStages?.[stage] || stage || copy.leadStages?.new,
  };
}

function getOpportunityPipelineStageDisplay(lead, modeCopy, { stageRelation = "opportunity_stage" } = {}) {
  if (lead?.status === "inactive") {
    return null;
  }

  const stage = lead?.[stageRelation];
  if (stage?.name) {
    return {
      key: stage.stage_type || "open",
      label: stage.name,
      color: stage.color || null,
    };
  }

  return {
    key: "open",
    label: modeCopy?.noPipeline || "No pipeline",
  };
}

function getOpportunityPipelineLabel(lead, modeCopy, { pipelineRelation = "opportunity_pipeline" } = {}) {
  return lead?.[pipelineRelation]?.name || modeCopy?.noPipeline || "No pipeline";
}

function getContactLabel(contact) {
  if (!contact) return "";
  const fullName = contact?.full_name || [contact?.first_name, contact?.last_name].filter(Boolean).join(" ");
  return fullName || contact?.email || contact?.phone || "-";
}

function getCompanyLabel(company) {
  if (!company) return "";
  return company?.name || company?.legal_name || company?.email || company?.phone || "-";
}

function getSearchText(...values) {
  return values
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .join(" ");
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "-";
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numberValue);
}

function getUserLabel(user) {
  if (!user) return "";
  return user.name || user.email || `User ${user.id}`;
}

function getLeadOwnerLabel(lead, pageCopy) {
  return getUserLabel(lead?.owner) || pageCopy.assignment.unassigned;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getLeadRelatedContacts(lead) {
  if (Array.isArray(lead?.related_contacts) && lead.related_contacts.length) {
    return lead.related_contacts;
  }
  return lead?.contact ? [{ ...lead.contact, is_primary_contact: true }] : [];
}

function getLeadRelatedCompanies(lead) {
  if (Array.isArray(lead?.related_companies) && lead.related_companies.length) {
    return lead.related_companies;
  }
  return lead?.company ? [lead.company] : [];
}

function getEntityInitials(label) {
  const words = String(label || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return (words[0]?.[0] || "?").toUpperCase() + (words[1]?.[0] || "").toUpperCase();
}

function normalizeImportHeader(header) {
  return String(header || "")
    .replace(/^\uFEFF/, "")
    .replace(/\s+\((required|optional|conditional|obligatorio|opcional|condicional)\)\s*$/i, "")
    .trim();
}

function isBlankImportValue(value) {
  return value === undefined || value === null || String(value).trim() === "";
}

function hasImportRowData(row) {
  return Object.entries(row).some(([key, value]) => key !== "_row_number" && !isBlankImportValue(value));
}

function getRequirementLabel(pageCopy, requirement) {
  return pageCopy.import.requirements?.[requirement] || requirement;
}

function downloadLeadImportTemplate(pageCopy) {
  const workbook = XLSX.utils.book_new();
  const headers = CRM_IMPORT_TEMPLATE_COLUMNS.map((column) => column.key);
  const importSheet = XLSX.utils.aoa_to_sheet([headers]);
  importSheet["!cols"] = headers.map((header) => ({ wch: Math.min(Math.max(header.length + 2, 16), 32) }));
  importSheet["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}1` };

  const guideRows = [
    [
      pageCopy.import.guideHeaders.column,
      pageCopy.import.guideHeaders.group,
      pageCopy.import.guideHeaders.requirement,
      pageCopy.import.guideHeaders.notes,
      pageCopy.import.guideHeaders.example,
    ],
    ...CRM_IMPORT_TEMPLATE_COLUMNS.map((column) => [
      column.key,
      column.group,
      getRequirementLabel(pageCopy, column.requirement),
      column.notes,
      column.example,
    ]),
  ];
  const guideSheet = XLSX.utils.aoa_to_sheet(guideRows);
  guideSheet["!cols"] = [{ wch: 28 }, { wch: 14 }, { wch: 16 }, { wch: 72 }, { wch: 28 }];
  guideSheet["!autofilter"] = {
    ref: `A1:${XLSX.utils.encode_col(guideRows[0].length - 1)}${guideRows.length}`,
  };

  const rulesSheet = XLSX.utils.aoa_to_sheet([
    [pageCopy.import.rulesTitle],
    [pageCopy.import.rules.leadTitle],
    [pageCopy.import.rules.company],
    [pageCopy.import.rules.contact],
    [pageCopy.import.rules.existingIds],
    [pageCopy.import.rules.ownerUpdate],
    [pageCopy.import.rules.dates],
    [pageCopy.import.rules.upload],
  ]);
  rulesSheet["!cols"] = [{ wch: 110 }];

  XLSX.utils.book_append_sheet(workbook, importSheet, pageCopy.import.sheetName);
  XLSX.utils.book_append_sheet(workbook, guideSheet, pageCopy.import.guideSheetName);
  XLSX.utils.book_append_sheet(workbook, rulesSheet, pageCopy.import.rulesSheetName);
  XLSX.writeFile(workbook, "crm-leads-import-template.xlsx", { compression: true });
}

function getLeadExportValue(lead, columnKey) {
  const [group, field] = String(columnKey || "").split(".");
  if (group === "company") return lead?.company?.[field] ?? "";
  if (group === "contact") {
    if (field === "customer_status") return lead?.contact?.customer_status ?? lead?.contact?.status ?? "";
    return lead?.contact?.[field] ?? "";
  }

  if (group !== "lead") return "";
  if (field === "current_owner_user_id") return lead?.owner_user_id ?? lead?.owner?.id ?? "";
  if (field === "current_owner_name") return lead?.owner?.name ?? "";
  if (field === "current_owner_email") return lead?.owner?.email ?? "";
  if (field === "new_owner_user_id") return "";
  return lead?.[field] ?? "";
}

function buildLeadBulkExportRows(leads) {
  return leads.map((lead) =>
    Object.fromEntries(
      CRM_LEADS_BULK_EXPORT_COLUMNS.map((column) => [column, getLeadExportValue(lead, column)])
    )
  );
}

async function fetchAllLeadsForBulkExport() {
  const allRows = [];
  let offset = 0;
  let total = null;

  while (total === null || allRows.length < total) {
    const page = await fetchCrmLeads({
      limit: LEADS_EXPORT_PAGE_SIZE,
      offset,
      export: 1,
    });
    const pageRows = Array.isArray(page?.rows) ? page.rows : [];
    allRows.push(...pageRows);
    total = Number(page?.total || allRows.length);
    const pageLimit = Number(page?.limit || LEADS_EXPORT_PAGE_SIZE);
    if (!pageRows.length || pageLimit <= 0) break;
    offset += pageLimit;
  }

  return allRows;
}

function downloadLeadBulkExport(leads, pageCopy) {
  const workbook = XLSX.utils.book_new();
  const exportRows = buildLeadBulkExportRows(leads);
  const worksheet = XLSX.utils.json_to_sheet(exportRows, {
    header: CRM_LEADS_BULK_EXPORT_COLUMNS,
  });
  worksheet["!cols"] = CRM_LEADS_BULK_EXPORT_COLUMNS.map((header) => ({
    wch: Math.min(Math.max(header.length + 2, 16), 34),
  }));
  worksheet["!autofilter"] = {
    ref: `A1:${XLSX.utils.encode_col(CRM_LEADS_BULK_EXPORT_COLUMNS.length - 1)}${exportRows.length + 1}`,
  };
  XLSX.utils.book_append_sheet(workbook, worksheet, pageCopy.import.exportSheetName);
  XLSX.writeFile(workbook, pageCopy.import.exportFileName, { compression: true });
}

async function parseLeadImportFile(file) {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array", raw: false });
  const sheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[sheetName];
  if (!worksheet) return { rows: [], unknownHeaders: [] };

  const rawRows = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
  const headerSet = new Set();
  rawRows.forEach((row) => {
    Object.keys(row || {}).forEach((header) => {
      const normalized = normalizeImportHeader(header);
      if (normalized) headerSet.add(normalized);
    });
  });

  const unknownHeaders = Array.from(headerSet).filter((header) => !CRM_IMPORT_COLUMN_KEYS.has(header));
  const rows = rawRows
    .map((rawRow, index) => {
      const row = { _row_number: index + 2 };

      Object.entries(rawRow || {}).forEach(([header, value]) => {
        const normalized = normalizeImportHeader(header);
        if (CRM_IMPORT_COLUMN_KEYS.has(normalized)) {
          row[normalized] = value;
        }
      });

      return row;
    })
    .filter(hasImportRowData);

  return { rows, unknownHeaders };
}

function formatImportApiError(err, pageCopy) {
  if (err?.unknownHeaders?.length) {
    return pageCopy.import.unknownColumns(err.unknownHeaders.slice(0, 8).join(", "));
  }

  const responseError = err?.response?.data?.error;
  const rowErrors = err?.response?.data?.errors;
  const importVersion =
    err?.response?.data?.import_version ||
    err?.response?.headers?.["x-crm-import-version"] ||
    err?.response?.headers?.get?.("x-crm-import-version");
  const versionSuffix = importVersion ? ` Import version: ${importVersion}.` : "";

  if (Array.isArray(rowErrors) && rowErrors.length) {
    const firstErrors = rowErrors
      .slice(0, 5)
      .map((item) => pageCopy.import.rowError(item.row, item.column, item.message))
      .join(" ");
    return `${[responseError, firstErrors].filter(Boolean).join(" ")}${versionSuffix}`;
  }

  return `${responseError || err?.message || pageCopy.import.error}${versionSuffix}`;
}

function getImportActionLabel(pageCopy, action) {
  return pageCopy.import.actions?.[action] || pageCopy.import.actions?.unknown || action || "-";
}

export default function Leads({ opportunityMode = false }) {
  const copy = useCrmCopy();
  const pageCopy = copy.entities.leads;
  const modeCopy = opportunityMode ? pageCopy.opportunities : null;
  const pipelineCopy = opportunityMode ? pageCopy.opportunities : pageCopy.pipeline || pageCopy.opportunities || {};
  const pipelineEntityType = opportunityMode ? "opportunity" : "lead";
  const pipelineIdField = opportunityMode ? "opportunity_pipeline_id" : "lead_pipeline_id";
  const pipelineStageIdField = opportunityMode ? "opportunity_stage_id" : "lead_pipeline_stage_id";
  const pipelineRelation = opportunityMode ? "opportunity_pipeline" : "lead_pipeline";
  const pipelineStageRelation = opportunityMode ? "opportunity_stage" : "lead_pipeline_stage";
  const mergeCopy = modeCopy?.merge || pageCopy.merge;
  const pageTitle = opportunityMode ? copy.sections.opportunities.title : copy.sections.leads.title;
  const pageClassName = opportunityMode
    ? "crm-page crm-page--compactTable crm-page--leads crm-page--opportunities"
    : "crm-page crm-page--compactTable crm-page--leads";
  const entityCopy = {
    detailView: modeCopy?.view || pageCopy.detail.view,
    detailTitle: modeCopy?.detailTitle || pageCopy.detail.title,
    detailLoadError: modeCopy?.detailLoadError || pageCopy.detail.loadError,
    noItem: modeCopy?.noItem || copy.common.noLead,
    fieldTitle: modeCopy?.fieldTitle || pageCopy.fields.title,
    selectItem: modeCopy?.selectItem || pageCopy.selection.selectLead,
    singleOnly: modeCopy?.singleOnly || pageCopy.selection.singleOnly,
    workPermission: modeCopy?.workPermission || pageCopy.selection.workPermission,
    assignPermission: modeCopy?.assignPermission || pageCopy.selection.assignPermission,
    mergeMinimum: modeCopy?.mergeMinimum || pageCopy.selection.mergeMinimum,
    mergePermission: modeCopy?.mergePermission || pageCopy.selection.mergePermission,
    assignmentChange: modeCopy?.assignmentChange || pageCopy.assignment.change,
    assignmentAssignTitle: modeCopy?.assignmentAssignTitle || pageCopy.assignment.assignTitle,
    assignmentMultipleTitle: modeCopy?.assignmentMultipleTitle || pageCopy.assignment.assignMultipleTitle,
    assignmentChangeTitle: modeCopy?.assignmentChangeTitle || pageCopy.assignment.changeTitle,
    assignmentItems: modeCopy?.assignmentItems || pageCopy.assignment.leads,
    selectedItems: modeCopy?.selectedItems || pageCopy.assignment.selectedLeads,
    assignmentError: modeCopy?.assignmentError || pageCopy.assignment.assignError,
    history: modeCopy?.history || pageCopy.assignment.history,
    historyError: modeCopy?.historyError || pageCopy.assignment.historyError,
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const importInputRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const selectAllRef = useRef(null);
  const openedDetailQueryRef = useRef("");
  const [rows, setRows] = useState([]);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [opportunityPipelines, setOpportunityPipelines] = useState([]);
  const [opportunityPipelineFilter, setOpportunityPipelineFilter] = useState(ALL_PIPELINES_FILTER);
  const [opportunityViewMode, setOpportunityViewMode] = useState("table");
  const [searchDraft, setSearchDraft] = useState("");
  const [query, setQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importConfirming, setImportConfirming] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [bulkMenuOpen, setBulkMenuOpen] = useState(false);
  const [error, setError] = useState("");
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergeError, setMergeError] = useState("");
  const [unmergeSaving, setUnmergeSaving] = useState(false);
  const [importError, setImportError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [pendingImportRows, setPendingImportRows] = useState([]);
  const [importPreviewFileName, setImportPreviewFileName] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactPickerQuery, setContactPickerQuery] = useState("");
  const [contactPickerOptions, setContactPickerOptions] = useState([]);
  const [contactPickerLoading, setContactPickerLoading] = useState(false);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const [companyPickerQuery, setCompanyPickerQuery] = useState("");
  const [companyPickerOptions, setCompanyPickerOptions] = useState([]);
  const [companyPickerLoading, setCompanyPickerLoading] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactSaving, setContactSaving] = useState(false);
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [companyForm, setCompanyForm] = useState(emptyCompanyForm);
  const [companySaving, setCompanySaving] = useState(false);
  const [relationshipPrompt, setRelationshipPrompt] = useState(null);
  const [relationshipSaving, setRelationshipSaving] = useState(false);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [assignmentLeads, setAssignmentLeads] = useState([]);
  const [assignmentOwnerId, setAssignmentOwnerId] = useState("");
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLead, setHistoryLead] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailLead, setDetailLead] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [stageGuideOpen, setStageGuideOpen] = useState(false);

  const stageOptions = useMemo(
    () => {
      const stages = opportunityMode ? ["qualified", "proposal", "won", "lost"] : ["new", "qualified", "proposal", "won", "lost"];
      return stages.map((stage) => ({
        value: stage,
        label: copy.leadStages?.[stage] || stage,
      }));
    },
    [copy.leadStages, opportunityMode]
  );

  const activeOpportunityPipelines = useMemo(
    () => opportunityPipelines.filter((pipeline) => pipeline.status !== "inactive"),
    [opportunityPipelines]
  );
  const selectedOpportunityPipeline = useMemo(
    () => opportunityPipelines.find((pipeline) => String(pipeline.id) === String(opportunityPipelineFilter)) || null,
    [opportunityPipelineFilter, opportunityPipelines]
  );
  const defaultOpportunityPipeline = useMemo(
    () =>
      activeOpportunityPipelines.find((pipeline) => pipeline.is_default && pipeline.entity_type === pipelineEntityType) ||
      activeOpportunityPipelines.find((pipeline) => pipeline.is_default) ||
      activeOpportunityPipelines.find((pipeline) => pipeline.entity_type === pipelineEntityType) ||
      activeOpportunityPipelines[0] ||
      null,
    [activeOpportunityPipelines, pipelineEntityType]
  );
  const formOpportunityPipeline = useMemo(
    () => opportunityPipelines.find((pipeline) => String(pipeline.id) === String(form[pipelineIdField])) || null,
    [form, pipelineIdField, opportunityPipelines]
  );
  const formOpportunityStageOptions = useMemo(
    () => (formOpportunityPipeline?.stages || []).filter((stage) => stage.status !== "inactive"),
    [formOpportunityPipeline]
  );

  const leadStatusOptions = useMemo(
    () =>
      ["open", "inactive", "won", "lost"].map((status) => ({
        value: status,
        label: pageCopy.statuses?.[status] || status,
      })),
    [pageCopy.statuses]
  );

  const statusOptions = useMemo(
    () => [
      { value: "active", label: copy.common.active },
      { value: "inactive", label: copy.common.inactive },
    ],
    [copy.common.active, copy.common.inactive]
  );

  const detailStageDisplay = detailLead ? getLeadStageDisplay(detailLead, copy, pageCopy) : null;
  const detailPipelineStageDisplay = detailLead
    ? getOpportunityPipelineStageDisplay(detailLead, pipelineCopy, { stageRelation: pipelineStageRelation })
    : null;
  const buildLeadReturnState = useCallback(
    (lead = detailLead) => {
      const params = new URLSearchParams(location.search);
      if (lead?.id) {
        params.delete("lead_id");
        params.delete("opportunity_id");
        params.set(opportunityMode ? "opportunity_id" : "lead_id", lead.id);
      }
      const search = params.toString();
      return {
        returnTo: {
          pathname: location.pathname,
          search: search ? `?${search}` : "",
          label: lead?.title || pageTitle,
        },
      };
    },
    [detailLead, location.pathname, location.search, opportunityMode, pageTitle]
  );
  const selectedLeadIdSet = useMemo(() => new Set(selectedLeadIds), [selectedLeadIds]);
  const selectedLeads = useMemo(
    () => rows.filter((lead) => selectedLeadIdSet.has(lead.id)),
    [rows, selectedLeadIdSet]
  );
  const selectedLead = selectedLeads.length === 1 ? selectedLeads[0] : null;
  const visibleLeadIds = useMemo(() => rows.map((lead) => lead.id).filter(Boolean), [rows]);
  const allVisibleSelected = visibleLeadIds.length > 0 && visibleLeadIds.every((id) => selectedLeadIdSet.has(id));
  const partiallySelected = selectedLeadIds.length > 0 && !allVisibleSelected;
  const selectedCanAssign = selectedLeads.length > 0 && selectedLeads.every((lead) => Boolean(lead.permissions?.can_assign));
  const selectedLeadCanWork = Boolean(selectedLead?.permissions?.can_work);
  const selectedCanConvertOpportunity = Boolean(
    !opportunityMode &&
      selectedLead &&
      selectedLeadCanWork &&
      selectedLead.stage === "new" &&
      !selectedLead.opportunity_started_at
  );
  const selectedCanMerge = selectedLeads.length >= 2 && selectedLeads.every((lead) => Boolean(lead.permissions?.can_work));
  const assignmentModalTitle =
    assignmentLeads.length > 1
      ? entityCopy.assignmentMultipleTitle
      : assignmentLeads[0]?.owner_user_id
        ? entityCopy.assignmentChangeTitle
        : entityCopy.assignmentAssignTitle;

  const loadLeads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const kanbanMode = opportunityViewMode === "kanban";
      const pipelineFilterValue =
        opportunityPipelineFilter !== ALL_PIPELINES_FILTER
          ? opportunityPipelineFilter === NO_PIPELINE_FILTER
            ? "none"
            : opportunityPipelineFilter
          : undefined;
      const data = await fetchCrmLeads({
        q: query,
        opportunity_only: opportunityMode ? 1 : undefined,
        include_inactive: showInactive ? 1 : 0,
        [pipelineIdField]: pipelineFilterValue,
        limit: kanbanMode ? 200 : LEADS_PAGE_SIZE,
        offset: kanbanMode ? 0 : pageIndex * LEADS_PAGE_SIZE,
      });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setTotalRows(Number(data?.total || 0));
    } catch (err) {
      console.error("[CRM] Leads load error:", err);
      setError(err?.response?.data?.error || pageCopy.loadError);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    opportunityMode,
    pipelineIdField,
    opportunityPipelineFilter,
    opportunityViewMode,
    pageCopy.loadError,
    pageIndex,
    query,
    showInactive,
  ]);

  const loadLookups = useCallback(async () => {
    try {
      const [contactsData, companiesData, usersData, pipelinesData] = await Promise.all([
        fetchCrmContacts({ limit: 200 }),
        fetchCrmCompanies({ limit: 200 }),
        fetchCrmAssignableUsers(),
        fetchCrmOpportunityPipelines({ include_inactive: 1, include_general: 1, entity_type: pipelineEntityType }),
      ]);
      setContacts(Array.isArray(contactsData?.rows) ? contactsData.rows : []);
      setCompanies(Array.isArray(companiesData?.rows) ? companiesData.rows : []);
      setAssignableUsers(Array.isArray(usersData?.rows) ? usersData.rows : []);
      setOpportunityPipelines(Array.isArray(pipelinesData?.rows) ? pipelinesData.rows : []);
    } catch (err) {
      console.error("[CRM] Lead lookups load error:", err);
      setContacts([]);
      setCompanies([]);
      setAssignableUsers([]);
      setOpportunityPipelines([]);
    }
  }, [pipelineEntityType]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    if (!defaultOpportunityPipeline) return;
    if (opportunityViewMode === "kanban" && opportunityPipelineFilter === ALL_PIPELINES_FILTER) {
      setOpportunityPipelineFilter(String(defaultOpportunityPipeline.id));
      setPageIndex(0);
    }
  }, [defaultOpportunityPipeline, opportunityPipelineFilter, opportunityViewMode]);

  useEffect(() => {
    setPageIndex(0);
  }, [opportunityPipelineFilter, opportunityViewMode]);

  useEffect(() => {
    if (!contactPickerOpen) return undefined;
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setContactPickerLoading(true);
      try {
        const data = await fetchCrmContacts({
          q: contactPickerQuery.trim(),
          limit: 200,
        });
        if (!cancelled) {
          setContactPickerOptions(Array.isArray(data?.rows) ? data.rows : []);
        }
      } catch (err) {
        console.error("[CRM] Contact picker load error:", err);
        if (!cancelled) {
          setContactPickerOptions(contacts);
        }
      } finally {
        if (!cancelled) {
          setContactPickerLoading(false);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [contactPickerOpen, contactPickerQuery, contacts]);

  useEffect(() => {
    if (!companyPickerOpen) return undefined;
    let cancelled = false;

    const timer = window.setTimeout(async () => {
      setCompanyPickerLoading(true);
      try {
        const data = await fetchCrmCompanies({
          q: companyPickerQuery.trim(),
          limit: 200,
        });
        if (!cancelled) {
          setCompanyPickerOptions(Array.isArray(data?.rows) ? data.rows : []);
        }
      } catch (err) {
        console.error("[CRM] Company picker load error:", err);
        if (!cancelled) {
          setCompanyPickerOptions(companies);
        }
      } finally {
        if (!cancelled) {
          setCompanyPickerLoading(false);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [companies, companyPickerOpen, companyPickerQuery]);

  useEffect(() => {
    const visibleIds = new Set(rows.map((lead) => lead.id));
    setSelectedLeadIds((current) => {
      const next = current.filter((id) => visibleIds.has(id));
      return next.length === current.length ? current : next;
    });
  }, [rows]);

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = partiallySelected;
    }
  }, [partiallySelected]);

  useEffect(() => {
    if (!bulkMenuOpen) return undefined;

    const handleClick = (event) => {
      if (!bulkMenuRef.current?.contains(event.target)) {
        setBulkMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setBulkMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [bulkMenuOpen]);

  const handleSearchSubmit = () => {
    setPageIndex(0);
    setQuery(searchDraft.trim());
  };

  const handleClearSearch = () => {
    setSearchDraft("");
    setPageIndex(0);
    setQuery("");
  };

  const pageStart = totalRows === 0 ? 0 : pageIndex * LEADS_PAGE_SIZE + 1;
  const pageEnd = Math.min((pageIndex + 1) * LEADS_PAGE_SIZE, totalRows);
  const hasPreviousPage = pageIndex > 0;
  const hasNextPage = pageEnd < totalRows;
  const importPreviewRows = Array.isArray(importPreview?.rows) ? importPreview.rows : [];
  const importPreviewErrors = Array.isArray(importPreview?.errors) ? importPreview.errors : [];
  const importResultErrors = [
    ...(Array.isArray(importResult?.errors) ? importResult.errors : []),
    ...(Array.isArray(importResult?.preview_errors) ? importResult.preview_errors : []),
  ];
  const previewRowsRemaining = Math.max(importPreviewRows.length - CRM_IMPORT_PREVIEW_ROW_LIMIT, 0);
  const previewErrorsRemaining = Math.max(importPreviewErrors.length - CRM_IMPORT_PREVIEW_ERROR_LIMIT, 0);
  const resultErrorsRemaining = Math.max(importResultErrors.length - CRM_IMPORT_PREVIEW_ERROR_LIMIT, 0);
  const detailContacts = getLeadRelatedContacts(detailLead);
  const detailCompanies = getLeadRelatedCompanies(detailLead);
  const canWorkDetailLead = Boolean(detailLead?.permissions?.can_work);
  const contactLookupOptions = useMemo(
    () => [...contacts, ...contactPickerOptions],
    [contactPickerOptions, contacts]
  );
  const companyLookupOptions = useMemo(
    () => [...companies, ...companyPickerOptions],
    [companies, companyPickerOptions]
  );
  const selectedFormContact = useMemo(
    () =>
      contactLookupOptions.find((contact) => String(contact.id) === String(form.contact_customer_id)) ||
      (String(editingLead?.contact_customer_id || "") === String(form.contact_customer_id || "")
        ? editingLead?.contact
        : null),
    [contactLookupOptions, editingLead, form.contact_customer_id]
  );
  const selectedFormCompany = useMemo(
    () =>
      companyLookupOptions.find((company) => String(company.id) === String(form.company_id)) ||
      (String(editingLead?.company_id || "") === String(form.company_id || "") ? editingLead?.company : null),
    [companyLookupOptions, editingLead, form.company_id]
  );
  const selectedFormContactLabel = form.contact_customer_id
    ? getContactLabel(selectedFormContact) || `#${form.contact_customer_id}`
    : copy.common.noContact;
  const selectedFormCompanyLabel = form.company_id
    ? getCompanyLabel(selectedFormCompany) || `#${form.company_id}`
    : copy.common.noCompany;
  const filteredContactOptions = useMemo(() => {
    const needle = contactPickerQuery.trim().toLowerCase();
    if (!needle) return contactPickerOptions;

    return contactPickerOptions.filter((contact) =>
      getSearchText(
        getContactLabel(contact),
        contact.email,
        contact.phone,
        contact.document_number,
        contact.country,
        contact.city,
        contact.job_title,
        contact.department,
        contact.company?.name,
        contact.company?.legal_name
      ).includes(needle)
    );
  }, [contactPickerOptions, contactPickerQuery]);
  const filteredCompanyOptions = useMemo(() => {
    const needle = companyPickerQuery.trim().toLowerCase();
    if (!needle) return companyPickerOptions;

    return companyPickerOptions.filter((company) =>
      getSearchText(
        getCompanyLabel(company),
        company.legal_name,
        company.tax_id,
        company.website,
        company.email,
        company.phone,
        company.industry,
        company.city,
        company.country
      ).includes(needle)
    );
  }, [companyPickerOptions, companyPickerQuery]);
  const findContactOptionById = (contactId) =>
    contactLookupOptions.find((contact) => String(contact.id) === String(contactId));
  const findCompanyOptionById = (companyId) =>
    companyLookupOptions.find((company) => String(company.id) === String(companyId));
  const contactNeedsCompanyLink = (contact, companyId) =>
    Boolean(contact?.id && companyId && String(contact.company_id || "") !== String(companyId));
  const upsertContactLookup = (contact) => {
    if (!contact?.id) return;
    setContacts((prev) => {
      const next = prev.filter((item) => Number(item.id) !== Number(contact.id));
      return [...next, contact].sort((a, b) => getContactLabel(a).localeCompare(getContactLabel(b)));
    });
    setContactPickerOptions((prev) => {
      const next = prev.filter((item) => Number(item.id) !== Number(contact.id));
      return [...next, contact].sort((a, b) => getContactLabel(a).localeCompare(getContactLabel(b)));
    });
  };
  const requestContactCompanyLink = ({ contact, company, rollback }) => {
    if (!contact?.id || !company?.id) return false;
    if (!contactNeedsCompanyLink(contact, company.id)) return false;

    const currentCompany =
      contact.company?.name ||
      contact.company?.legal_name ||
      getCompanyLabel(findCompanyOptionById(contact.company_id));

    setRelationshipPrompt({
      contact,
      company,
      rollback,
      currentCompany,
    });
    return true;
  };

  const openCreate = () => {
    setEditingLead(null);
    const creationPipeline =
      opportunityPipelineFilter !== ALL_PIPELINES_FILTER && opportunityPipelineFilter !== NO_PIPELINE_FILTER
        ? selectedOpportunityPipeline
        : null;
    const creationStage = creationPipeline?.stages?.find((stage) => stage.status !== "inactive") || null;
    const baseForm = {
      ...emptyForm,
      [pipelineIdField]: creationPipeline?.id || "",
      [pipelineStageIdField]: creationStage?.id || "",
    };
    setForm(
      opportunityMode
        ? {
            ...baseForm,
            stage: "qualified",
            status: "open",
          }
        : baseForm
    );
    setContactPickerQuery("");
    setCompanyPickerQuery("");
    setModalOpen(true);
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setForm(buildFormFromLead(lead));
    setContactPickerQuery("");
    setCompanyPickerQuery("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setContactPickerOpen(false);
    setCompanyPickerOpen(false);
    setRelationshipPrompt(null);
    setEditingLead(null);
    setForm(emptyForm);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeadStageChange = (value) => {
    setForm((prev) => ({
      ...prev,
      stage: value,
      status: prev.status === "inactive" ? prev.status : getDefaultLeadStatusForStage(value),
    }));
  };

  const handleOpportunityPipelineChange = (value) => {
    const pipeline = opportunityPipelines.find((item) => String(item.id) === String(value));
    const firstStage = pipeline?.stages?.find((stage) => stage.status !== "inactive");
    setForm((prev) => ({
      ...prev,
      [pipelineIdField]: value || "",
      [pipelineStageIdField]: value ? firstStage?.id || "" : "",
    }));
  };

  const handleOpportunityStageChange = (value) => {
    setForm((prev) => ({
      ...prev,
      [pipelineStageIdField]: value || "",
    }));
  };

  const handleContactFieldChange = (field, value) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanyFieldChange = (field, value) => {
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleLeadContactChange = (value) => {
    if (value === CREATE_CONTACT_VALUE) {
      setContactForm({
        ...emptyContactForm,
        company_id: form.company_id || "",
        source: form.source || "",
      });
      setContactModalOpen(true);
      return;
    }

    handleFieldChange("contact_customer_id", value);
    if (!value || !form.company_id) return;

    const contact = findContactOptionById(value);
    const company = selectedFormCompany || findCompanyOptionById(form.company_id);
    requestContactCompanyLink({
      contact,
      company,
      rollback: { contact_customer_id: form.contact_customer_id || "" },
    });
  };

  const handleLeadCompanyChange = (value) => {
    if (value === CREATE_COMPANY_VALUE) {
      setCompanyForm(emptyCompanyForm);
      setCompanyModalOpen(true);
      return;
    }

    handleFieldChange("company_id", value);
    if (!value || !form.contact_customer_id) return;

    const contact = selectedFormContact || findContactOptionById(form.contact_customer_id);
    const company = findCompanyOptionById(value);
    requestContactCompanyLink({
      contact,
      company,
      rollback: { company_id: form.company_id || "" },
    });
  };

  const handleConfirmRelationship = async () => {
    if (!relationshipPrompt?.contact?.id || !relationshipPrompt?.company?.id) return;
    setRelationshipSaving(true);
    setError("");

    try {
      const updated = await updateCrmContact(relationshipPrompt.contact.id, {
        company_id: relationshipPrompt.company.id,
      });
      upsertContactLookup(updated);
      setForm((prev) => ({
        ...prev,
        contact_customer_id: relationshipPrompt.contact.id,
        company_id: relationshipPrompt.company.id,
      }));
      setRelationshipPrompt(null);
      await loadLookups();
    } catch (err) {
      console.error("[CRM] Contact company link error:", err);
      setError(err?.response?.data?.error || pageCopy.relationship?.error || "Could not link contact and company.");
    } finally {
      setRelationshipSaving(false);
    }
  };

  const handleCancelRelationship = () => {
    if (relationshipSaving) return;
    const rollback = relationshipPrompt?.rollback || {};
    setForm((prev) => ({ ...prev, ...rollback }));
    setRelationshipPrompt(null);
  };

  const closeContactModal = () => {
    if (contactSaving) return;
    setContactModalOpen(false);
    setContactForm(emptyContactForm);
  };

  const closeCompanyModal = () => {
    if (companySaving) return;
    setCompanyModalOpen(false);
    setCompanyForm(emptyCompanyForm);
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeadIds((current) =>
      current.includes(leadId)
        ? current.filter((id) => id !== leadId)
        : [...current, leadId]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedLeadIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleLeadIds.includes(id));
      }

      return Array.from(new Set([...current, ...visibleLeadIds]));
    });
  };

  const clearLeadSelection = () => {
    setSelectedLeadIds([]);
  };

  const openAssignment = (leadOrLeads) => {
    const leads = Array.isArray(leadOrLeads) ? leadOrLeads.filter(Boolean) : [leadOrLeads].filter(Boolean);
    if (!leads.length) return;

    setAssignmentLeads(leads);
    setAssignmentOwnerId(leads.length === 1 ? leads[0]?.owner_user_id || "" : "");
    setAssignmentModalOpen(true);
  };

  const closeAssignmentModal = () => {
    if (assignmentSaving) return;
    setAssignmentModalOpen(false);
    setAssignmentLeads([]);
    setAssignmentOwnerId("");
  };

  const handleAssignmentSubmit = async (event) => {
    event.preventDefault();
    if (!assignmentLeads.length || !assignmentOwnerId) return;

    setAssignmentSaving(true);
    setError("");
    try {
      await Promise.all(assignmentLeads.map((lead) => assignCrmLead(lead.id, assignmentOwnerId)));
      setSelectedLeadIds([]);
      closeAssignmentModal();
      await loadLeads();
    } catch (err) {
      console.error("[CRM] Lead assignment error:", err);
      setError(err?.response?.data?.error || entityCopy.assignmentError);
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleSelectedView = () => {
    if (selectedLead) openLeadDetail(selectedLead);
  };

  const handleSelectedEdit = () => {
    if (selectedLead && selectedLeadCanWork) openEdit(selectedLead);
  };

  const handleSelectedAssignment = () => {
    if (selectedLeads.length && selectedCanAssign) openAssignment(selectedLeads);
  };

  const handleSelectedConvertOpportunity = async () => {
    if (!selectedCanConvertOpportunity) return;

    setSaving(true);
    setError("");
    try {
      await updateCrmLead(selectedLead.id, { stage: "qualified", status: "open" });
      setSelectedLeadIds([]);
      await loadLeads();
    } catch (err) {
      console.error("[CRM] Lead opportunity conversion error:", err);
      setError(err?.response?.data?.error || pageCopy.selection.convertOpportunityError);
    } finally {
      setSaving(false);
    }
  };

  const handleSelectedHistory = () => {
    if (selectedLead) openAssignmentHistory(selectedLead);
  };

  const openMergeModal = () => {
    if (!selectedCanMerge) return;
    setMergeError("");
    setMergeModalOpen(true);
  };

  const closeMergeModal = () => {
    if (mergeSaving) return;
    setMergeModalOpen(false);
    setMergeError("");
  };

  const handleMergeSubmit = async (payload) => {
    setMergeSaving(true);
    setMergeError("");
    setError("");
    try {
      await mergeCrmLeads(payload);
      setSelectedLeadIds([]);
      setMergeModalOpen(false);
      await loadLeads();
    } catch (err) {
      console.error("[CRM] Lead merge error:", err);
      setMergeError(err?.response?.data?.error || mergeCopy.error);
    } finally {
      setMergeSaving(false);
    }
  };

  const openAssignmentHistory = async (lead) => {
    setHistoryLead(lead);
    setAssignmentHistory([]);
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    setError("");

    try {
      const data = await fetchCrmLeadAssignmentHistory(lead.id);
      setAssignmentHistory(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      console.error("[CRM] Lead assignment history error:", err);
      setError(err?.response?.data?.error || entityCopy.historyError);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closeHistoryModal = () => {
    if (historyLoading) return;
    setHistoryModalOpen(false);
    setHistoryLead(null);
    setAssignmentHistory([]);
  };

  const loadLeadDetail = useCallback(async (leadId) => {
    const data = await fetchCrmLead(leadId);
    setDetailLead(data);
    setRows((prev) => prev.map((item) => (Number(item.id) === Number(data.id) ? { ...item, ...data } : item)));
    return data;
  }, []);

  const openLeadDetail = useCallback(async (lead) => {
    if (!lead?.id) return;
    const leadId = String(lead.id);
    const nextParams = new URLSearchParams(searchParams);
    const currentId = nextParams.get("lead_id") || nextParams.get("opportunity_id");
    if (currentId !== leadId) {
      nextParams.delete("lead_id");
      nextParams.delete("opportunity_id");
      nextParams.set(opportunityMode ? "opportunity_id" : "lead_id", leadId);
      openedDetailQueryRef.current = leadId;
      setSearchParams(nextParams, { replace: true });
    }
    setDetailLead(lead);
    setDetailError("");
    setDetailModalOpen(true);
    setDetailLoading(true);

    try {
      await loadLeadDetail(lead.id);
    } catch (err) {
      console.error("[CRM] Lead detail load error:", err);
      setDetailError(err?.response?.data?.error || entityCopy.detailLoadError);
    } finally {
      setDetailLoading(false);
    }
  }, [entityCopy.detailLoadError, loadLeadDetail, opportunityMode, searchParams, setSearchParams]);

  const closeLeadDetail = () => {
    if (detailSaving) return;
    setDetailModalOpen(false);
    setDetailLead(null);
    setDetailError("");
    if (
      location.state?.returnTo?.pathname &&
      (searchParams.has("lead_id") || searchParams.has("opportunity_id"))
    ) {
      const returnTo = location.state.returnTo;
      navigate(`${returnTo.pathname}${returnTo.search || ""}`, {
        state: returnTo.state || undefined,
      });
      openedDetailQueryRef.current = "";
      return;
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("lead_id");
    nextParams.delete("opportunity_id");
    setSearchParams(nextParams, { replace: true });
    openedDetailQueryRef.current = "";
  };

  const openDetailEdit = () => {
    if (!detailLead || !canWorkDetailLead) return;
    const leadToEdit = detailLead;
    setDetailModalOpen(false);
    setDetailLead(null);
    setDetailError("");
    openEdit(leadToEdit);
  };

  useEffect(() => {
    const leadId = searchParams.get("lead_id") || searchParams.get("opportunity_id");
    if (!leadId || openedDetailQueryRef.current === leadId) return;
    openedDetailQueryRef.current = leadId;
    openLeadDetail({ id: leadId });
  }, [openLeadDetail, searchParams]);

  const handleUnassignLeadContact = async (contact) => {
    if (!detailLead?.id || !contact?.id || detailSaving) return;
    if (Number(contact.id) !== Number(detailLead.contact_customer_id)) return;

    setDetailSaving(true);
    setDetailError("");

    try {
      const updated = await updateCrmLead(detailLead.id, { contact_customer_id: null });
      setDetailLead(updated);
      setRows((prev) =>
        prev.map((item) => (Number(item.id) === Number(updated.id) ? { ...item, ...updated } : item))
      );
      await loadLeads();
    } catch (err) {
      console.error("[CRM] Lead contact unassign error:", err);
      setDetailError(err?.response?.data?.error || pageCopy.detail.removeContactError);
    } finally {
      setDetailSaving(false);
    }
  };

  const handleUnassignLeadCompany = async (company) => {
    if (!detailLead?.id || !company?.id || detailSaving) return;
    if (Number(company.id) !== Number(detailLead.company_id)) return;

    setDetailSaving(true);
    setDetailError("");

    try {
      const updated = await updateCrmLead(detailLead.id, { company_id: null });
      setDetailLead(updated);
      setRows((prev) =>
        prev.map((item) => (Number(item.id) === Number(updated.id) ? { ...item, ...updated } : item))
      );
      await loadLeads();
    } catch (err) {
      console.error("[CRM] Lead company unassign error:", err);
      setDetailError(err?.response?.data?.error || pageCopy.detail.removeCompanyError);
    } finally {
      setDetailSaving(false);
    }
  };

  const handleUnmergeLead = async () => {
    if (!detailLead?.id || unmergeSaving) return;

    setUnmergeSaving(true);
    setDetailError("");

    try {
      const result = await unmergeCrmLead(detailLead.id);
      if (result?.record) {
        setDetailLead(result.record);
      } else {
        await loadLeadDetail(detailLead.id);
      }
      await loadLeads();
    } catch (err) {
      console.error("[CRM] Lead unmerge error:", err);
      setDetailError(err?.response?.data?.error || mergeCopy.unmergeError);
    } finally {
      setUnmergeSaving(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadLeadImportTemplate(pageCopy);
  };

  const handleBulkTemplate = () => {
    setBulkMenuOpen(false);
    handleDownloadTemplate();
  };

  const openImportPicker = () => {
    if (importing || importConfirming) return;
    importInputRef.current?.click();
  };

  const handleBulkImport = () => {
    setBulkMenuOpen(false);
    openImportPicker();
  };

  const handleExportLeads = async () => {
    if (exporting) return;

    setBulkMenuOpen(false);
    setExporting(true);
    setImportError("");
    setError("");

    try {
      const leads = await fetchAllLeadsForBulkExport();
      downloadLeadBulkExport(leads, pageCopy);
      setImportResult(null);
    } catch (err) {
      console.error("[CRM] Leads bulk export error:", err);
      setImportError(err?.response?.data?.error || pageCopy.import.exportError);
    } finally {
      setExporting(false);
    }
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setImporting(true);
    setImportError("");
    setImportResult(null);
    setImportPreview(null);
    setPendingImportRows([]);
    setImportPreviewFileName("");
    setError("");

    try {
      const parsed = await parseLeadImportFile(file);
      if (parsed.unknownHeaders.length) {
        const unknownError = new Error(pageCopy.import.error);
        unknownError.unknownHeaders = parsed.unknownHeaders;
        throw unknownError;
      }

      if (!parsed.rows.length) {
        throw new Error(pageCopy.import.emptyFile);
      }

      const result = await importCrmLeads({ rows: parsed.rows, dry_run: true });
      setImportPreview(result);
      setPendingImportRows(parsed.rows);
      setImportPreviewFileName(file.name || "");
    } catch (err) {
      console.error("[CRM] Lead import error:", err);
      setImportError(formatImportApiError(err, pageCopy));
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImportPreview = () => {
    if (importing || importConfirming) return;
    setImportPreview(null);
    setPendingImportRows([]);
    setImportPreviewFileName("");
  };

  const handleConfirmImport = async () => {
    if (importConfirming || !pendingImportRows.length || !importPreviewRows.length) return;

    const readyRows = new Set(
      importPreviewRows
        .map((row) => Number(row.row))
        .filter((rowNumber) => Number.isFinite(rowNumber))
    );
    const rowsToImport = pendingImportRows.filter((row) => readyRows.has(Number(row._row_number)));
    if (!rowsToImport.length) return;

    setImportConfirming(true);
    setImportError("");
    setImportResult(null);
    setError("");

    try {
      const result = await importCrmLeads({ rows: rowsToImport });
      setImportResult({
        ...result,
        preview_errors: importPreviewErrors,
        original_total_rows: importPreview?.total_rows,
      });
      setImportPreview(null);
      setPendingImportRows([]);
      setImportPreviewFileName("");
      setPageIndex(0);
      if (pageIndex === 0) {
        await loadLeads();
      }
      await loadLookups();
    } catch (err) {
      console.error("[CRM] Lead import confirm error:", err);
      setImportError(formatImportApiError(err, pageCopy));
    } finally {
      setImportConfirming(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (form.contact_customer_id && form.company_id) {
      const contact = selectedFormContact || findContactOptionById(form.contact_customer_id);
      const company = selectedFormCompany || findCompanyOptionById(form.company_id);
      const shouldLink = requestContactCompanyLink({
        contact,
        company,
        rollback: {},
      });
      if (shouldLink) return;
    }

    setSaving(true);
    setError("");

    const payload = {
      ...form,
      status: form.status || getDefaultLeadStatusForStage(form.stage),
      contact_customer_id: form.contact_customer_id || null,
      company_id: form.company_id || null,
      estimated_value: form.estimated_value === "" ? null : form.estimated_value,
      expected_close_date: form.expected_close_date || null,
      ...(opportunityMode
        ? {
            opportunity_pipeline_id: form.opportunity_pipeline_id || null,
            opportunity_stage_id: form.opportunity_stage_id || null,
          }
        : {
            lead_pipeline_id: form.lead_pipeline_id || null,
            lead_pipeline_stage_id: form.lead_pipeline_stage_id || null,
          }),
    };

    try {
      if (editingLead?.id) {
        await updateCrmLead(editingLead.id, payload);
        await loadLeads();
      } else {
        await createCrmLead(payload);
        setPageIndex(0);
        if (pageIndex === 0) {
          await loadLeads();
        }
      }
      setModalOpen(false);
      setEditingLead(null);
      setForm(emptyForm);
    } catch (err) {
      console.error("[CRM] Lead save error:", err);
      setError(err?.response?.data?.error || pageCopy.saveError);
    } finally {
      setSaving(false);
    }
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactSaving(true);
    setError("");
    const previousContactId = form.contact_customer_id || "";

    const payload = {
      ...contactForm,
      company_id: contactForm.company_id || null,
    };

    try {
      const created = await createCrmContact(payload);
      setContacts((prev) => {
        const next = prev.filter((contact) => Number(contact.id) !== Number(created.id));
        return [...next, created].sort((a, b) => getContactLabel(a).localeCompare(getContactLabel(b)));
      });
      setForm((prev) => ({
        ...prev,
        contact_customer_id: created.id,
        company_id: prev.company_id || created.company_id || "",
      }));
      if (form.company_id) {
        requestContactCompanyLink({
          contact: created,
          company: selectedFormCompany || findCompanyOptionById(form.company_id),
          rollback: { contact_customer_id: previousContactId },
        });
      }
      setContactModalOpen(false);
      setContactForm(emptyContactForm);
      await loadLookups();
    } catch (err) {
      console.error("[CRM] Inline contact create error:", err);
      setError(err?.response?.data?.error || pageCopy.createContactError);
    } finally {
      setContactSaving(false);
    }
  };

  const handleCompanySubmit = async (event) => {
    event.preventDefault();
    setCompanySaving(true);
    setError("");
    const previousCompanyId = form.company_id || "";

    try {
      const created = await createCrmCompany(companyForm);
      setCompanies((prev) => {
        const next = prev.filter((company) => Number(company.id) !== Number(created.id));
        return [...next, created].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      });
      setForm((prev) => ({ ...prev, company_id: created.id }));
      setContactForm((prev) => ({ ...prev, company_id: created.id }));
      if (form.contact_customer_id) {
        requestContactCompanyLink({
          contact: selectedFormContact || findContactOptionById(form.contact_customer_id),
          company: created,
          rollback: { company_id: previousCompanyId },
        });
      }
      setCompanyModalOpen(false);
      setCompanyForm(emptyCompanyForm);
      await loadLookups();
    } catch (err) {
      console.error("[CRM] Inline company create error:", err);
      setError(err?.response?.data?.error || pageCopy.createCompanyError);
    } finally {
      setCompanySaving(false);
    }
  };

  return (
    <main className={pageClassName}>
      <header className="crm-pageHeader">
        <div>
          <p className="crm-eyebrow">{copy.common.crm}</p>
          <h1>{pageTitle}</h1>
        </div>

        <div className="crm-pageHeaderActions">
          <button
            type="button"
            className="crm-iconButton crm-pageInfoButton"
            onClick={() => setStageGuideOpen(true)}
            title={pageCopy.stageLegendTitle}
            aria-label={pageCopy.stageLegendTitle}
          >
            <FiInfo aria-hidden="true" />
          </button>
          {!opportunityMode ? (
            <div className="crm-bulkMenu" ref={bulkMenuRef}>
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setBulkMenuOpen((prev) => !prev)}
                disabled={importing || importConfirming || exporting}
                aria-haspopup="menu"
                aria-expanded={bulkMenuOpen}
              >
                <FiUpload aria-hidden="true" />
                <span>
                  {importing
                    ? pageCopy.import.previewing
                    : importConfirming
                      ? pageCopy.import.importing
                      : exporting
                        ? pageCopy.import.exporting
                        : pageCopy.import.bulkOptions}
                </span>
                <FiChevronDown aria-hidden="true" />
              </button>
              {bulkMenuOpen ? (
                <div className="crm-bulkMenuPanel" role="menu">
                  <button type="button" role="menuitem" onClick={handleBulkTemplate}>
                    <FiDownload aria-hidden="true" />
                    <span>{pageCopy.import.downloadTemplate}</span>
                  </button>
                  <button type="button" role="menuitem" onClick={handleBulkImport} disabled={importing || importConfirming}>
                    <FiUpload aria-hidden="true" />
                    <span>{pageCopy.import.uploadFile}</span>
                  </button>
                  <button type="button" role="menuitem" onClick={handleExportLeads} disabled={exporting}>
                    <FiDownload aria-hidden="true" />
                    <span>{exporting ? pageCopy.import.exporting : pageCopy.import.exportLeads}</span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <button type="button" className="crm-ghostButton" onClick={openCreate}>
            <FiPlus aria-hidden="true" />
            <span>{modeCopy?.newTitle || pageCopy.newTitle}</span>
          </button>
          <input
            ref={importInputRef}
            className="crm-fileInput"
            type="file"
            accept={CRM_IMPORT_ACCEPT}
            onChange={handleImportFileChange}
          />
        </div>
      </header>

      <section className="crm-panel">
        <div className="crm-entityToolbar">
          <label className="crm-searchBox">
            <FiSearch aria-hidden="true" />
            <input
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  handleSearchSubmit();
                }
              }}
              placeholder={modeCopy?.searchPlaceholder || pageCopy.searchPlaceholder}
            />
          </label>
          <button type="button" className="crm-ghostButton" onClick={handleSearchSubmit} disabled={loading}>
            <FiSearch aria-hidden="true" />
            <span>{loading ? copy.common.loading : copy.common.search}</span>
          </button>
          <button
            type="button"
            className="crm-ghostButton"
            onClick={handleClearSearch}
            disabled={loading || (!query && !searchDraft)}
          >
            <FiX aria-hidden="true" />
            <span>{copy.common.clear}</span>
          </button>
        </div>

        <div className="crm-opportunityPipelineToolbar">
          <div className="crm-segmentedControl" aria-label={pipelineCopy?.kanbanView || "Pipeline view"}>
            <button
              type="button"
              className={opportunityViewMode === "table" ? "crm-segmentedControlItem--active" : ""}
              onClick={() => setOpportunityViewMode("table")}
            >
              <FiList aria-hidden="true" />
              <span>{pipelineCopy?.tableView || "Table"}</span>
            </button>
            <button
              type="button"
              className={opportunityViewMode === "kanban" ? "crm-segmentedControlItem--active" : ""}
              onClick={() => {
                if (opportunityPipelineFilter === ALL_PIPELINES_FILTER) {
                  setOpportunityPipelineFilter(defaultOpportunityPipeline ? String(defaultOpportunityPipeline.id) : NO_PIPELINE_FILTER);
                }
                setOpportunityViewMode("kanban");
              }}
            >
              <FiColumns aria-hidden="true" />
              <span>{pipelineCopy?.kanbanView || "Pipeline"}</span>
            </button>
          </div>
          <label className="crm-opportunityPipelineSelect">
            <span>{pipelineCopy?.pipeline || "Pipeline"}</span>
            <select
              value={opportunityPipelineFilter}
              onChange={(event) => {
                setOpportunityPipelineFilter(event.target.value);
                setPageIndex(0);
              }}
            >
              {opportunityViewMode === "table" ? (
                <option value={ALL_PIPELINES_FILTER}>{pipelineCopy?.allPipelines || "All pipelines"}</option>
              ) : null}
              <option value={NO_PIPELINE_FILTER}>{pipelineCopy?.noPipeline || "No pipeline"}</option>
              {activeOpportunityPipelines.map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                  {pipeline.is_default ? " *" : ""}
                </option>
              ))}
            </select>
          </label>
        </div>

        {importError ? <div className="crm-stateBanner is-error">{importError}</div> : null}

        {importPreview ? (
          <div className="crm-importPreview" aria-live="polite">
            <div className="crm-importPreviewHeader">
              <div>
                <strong>{pageCopy.import.previewTitle}</strong>
                {importPreviewFileName ? <span>{importPreviewFileName}</span> : null}
                <span>{pageCopy.import.previewSummary(importPreview)}</span>
              </div>
              <div className="crm-importPreviewActions">
                <button
                  type="button"
                  className="crm-ghostButton"
                  onClick={handleCancelImportPreview}
                  disabled={importing || importConfirming}
                >
                  <FiX aria-hidden="true" />
                  <span>{pageCopy.import.cancelPreview}</span>
                </button>
                <button
                  type="button"
                  className="crm-ghostButton"
                  onClick={handleConfirmImport}
                  disabled={importing || importConfirming || !importPreviewRows.length}
                >
                  <FiUpload aria-hidden="true" />
                  <span>{importConfirming ? pageCopy.import.importing : pageCopy.import.confirmImport}</span>
                </button>
              </div>
            </div>

            <div className="crm-importPreviewGrid">
              <section className="crm-importPreviewPane">
                <h3>{pageCopy.import.previewRowsTitle}</h3>
                <div className="crm-importPreviewList">
                  {importPreviewRows.length ? (
                    importPreviewRows.slice(0, CRM_IMPORT_PREVIEW_ROW_LIMIT).map((item) => (
                      <div key={`ready-${item.row}`} className="crm-importPreviewRow">
                        <strong>
                          {pageCopy.import.previewRow(
                            item,
                            getImportActionLabel(pageCopy, item.lead_action)
                          )}
                        </strong>
                        <span>
                          {pageCopy.import.previewMeta(
                            getImportActionLabel(pageCopy, item.company_action),
                            getImportActionLabel(pageCopy, item.contact_action)
                          )}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="crm-importPreviewEmpty">{pageCopy.import.previewEmptyRows}</div>
                  )}
                </div>
                {previewRowsRemaining ? (
                  <p className="crm-importPreviewMore">{pageCopy.import.moreRows(previewRowsRemaining)}</p>
                ) : null}
              </section>

              <section className="crm-importPreviewPane crm-importPreviewPane--errors">
                <h3>{pageCopy.import.previewErrorsTitle}</h3>
                <div className="crm-importPreviewList">
                  {importPreviewErrors.length ? (
                    importPreviewErrors.slice(0, CRM_IMPORT_PREVIEW_ERROR_LIMIT).map((item, index) => (
                      <div key={`error-${item.row}-${item.column}-${index}`} className="crm-importPreviewRow">
                        <strong>{pageCopy.import.previewError(item)}</strong>
                      </div>
                    ))
                  ) : (
                    <div className="crm-importPreviewEmpty">{pageCopy.import.previewNoErrors}</div>
                  )}
                </div>
                {previewErrorsRemaining ? (
                  <p className="crm-importPreviewMore">{pageCopy.import.moreErrors(previewErrorsRemaining)}</p>
                ) : null}
              </section>
            </div>
          </div>
        ) : null}

        {importResult ? (
          <div className="crm-stateBanner is-success">
            {pageCopy.import.success(importResult)}
          </div>
        ) : null}

        {importResult && importResultErrors.length ? (
          <div className="crm-importPreview crm-importPreview--result" aria-live="polite">
            <div className="crm-importPreviewHeader">
              <div>
                <strong>{pageCopy.import.resultErrorsTitle}</strong>
                <span>{pageCopy.import.resultErrorsSummary(importResultErrors.length)}</span>
              </div>
            </div>
            <div className="crm-importPreviewList">
              {importResultErrors.slice(0, CRM_IMPORT_PREVIEW_ERROR_LIMIT).map((item, index) => (
                <div key={`result-error-${item.row}-${item.column}-${index}`} className="crm-importPreviewRow">
                  <strong>{pageCopy.import.previewError(item)}</strong>
                </div>
              ))}
            </div>
            {resultErrorsRemaining ? (
              <p className="crm-importPreviewMore">{pageCopy.import.moreErrors(resultErrorsRemaining)}</p>
            ) : null}
          </div>
        ) : null}

        {error ? <div className="crm-stateBanner is-error">{error}</div> : null}

        <section className="crm-leadActionPanel" aria-label={modeCopy?.selectionTitle || pageCopy.selection.title}>
          <div className="crm-leadActionSummary">
            <strong>{modeCopy?.selectionTitle || pageCopy.selection.title}</strong>
            <span>
              {selectedLeadIds.length
                ? pageCopy.selection.selected(selectedLeadIds.length)
                : modeCopy?.noneSelected || pageCopy.selection.noneSelected}
            </span>
          </div>
          <div className="crm-leadActionControls">
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={handleSelectedView}
              disabled={!selectedLead}
              title={!selectedLead ? entityCopy.singleOnly : entityCopy.detailView}
              aria-label={entityCopy.detailView}
            >
              <FiEye aria-hidden="true" />
              <span>{entityCopy.detailView}</span>
            </button>
            <div className="crm-leadActionControl">
              <ZadarmaCallButton
                phones={selectedLead?.contact?.phone}
                label={selectedLead ? getContactLabel(selectedLead.contact) || selectedLead.title : ""}
                entityType="lead"
                entityId={selectedLead?.id}
                disabled={!selectedLead}
                disabledTitle={entityCopy.singleOnly}
                showWhenUnavailable
                className="crm-leadActionCall"
              />
              <span>{pageCopy.selection.call}</span>
            </div>
            <div className="crm-leadActionControl">
              <WhatsAppStartButton
                phone={selectedLead?.contact?.phone}
                label={selectedLead ? getContactLabel(selectedLead.contact) || selectedLead.title : ""}
                contactId={selectedLead?.contact?.id}
                leadId={selectedLead?.id}
                disabled={!selectedLead || !selectedLeadCanWork}
                disabledTitle={
                  !selectedLead
                    ? entityCopy.singleOnly
                    : !selectedLeadCanWork
                      ? entityCopy.workPermission
                      : ""
                }
                className="crm-leadActionMini"
                onStarted={(conversation) => {
                  if (conversation?.id) navigate(`/crm/activity?conversation_id=${conversation.id}`);
                }}
              />
              <span>WhatsApp</span>
            </div>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={handleSelectedEdit}
              disabled={!selectedLead || !selectedLeadCanWork}
              title={
                !selectedLead
                  ? entityCopy.singleOnly
                  : !selectedLeadCanWork
                    ? entityCopy.workPermission
                    : copy.common.edit
              }
              aria-label={copy.common.edit}
            >
              <FiEdit2 aria-hidden="true" />
              <span>{copy.common.edit}</span>
            </button>
            {!opportunityMode ? (
              <button
                type="button"
                className="crm-leadActionButton"
                onClick={handleSelectedConvertOpportunity}
                disabled={!selectedCanConvertOpportunity || saving}
                title={
                  !selectedLead
                    ? entityCopy.singleOnly
                    : !selectedLeadCanWork
                      ? entityCopy.workPermission
                      : selectedLead.stage !== "new" || selectedLead.opportunity_started_at
                        ? pageCopy.selection.convertOpportunityUnavailable
                        : pageCopy.selection.convertOpportunity
                }
                aria-label={pageCopy.selection.convertOpportunity}
              >
                <FiTarget aria-hidden="true" />
                <span>{pageCopy.selection.convertOpportunity}</span>
              </button>
            ) : null}
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={handleSelectedAssignment}
              disabled={!selectedLeadIds.length || !selectedCanAssign}
              title={
                !selectedLeadIds.length
                  ? pageCopy.selection.noneSelected
                  : !selectedCanAssign
                    ? entityCopy.assignPermission
                    : entityCopy.assignmentChange
              }
              aria-label={entityCopy.assignmentChange}
            >
              <FiUserPlus aria-hidden="true" />
              <span>{entityCopy.assignmentChange}</span>
            </button>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={openMergeModal}
              disabled={!selectedCanMerge}
              title={
                selectedLeads.length < 2
                  ? entityCopy.mergeMinimum
                  : !selectedCanMerge
                    ? entityCopy.mergePermission
                    : mergeCopy.title
              }
              aria-label={mergeCopy.title}
            >
              <FiGitMerge aria-hidden="true" />
              <span>{pageCopy.selection.merge}</span>
            </button>
            <button
              type="button"
              className="crm-leadActionButton"
              onClick={handleSelectedHistory}
              disabled={!selectedLead}
              title={!selectedLead ? entityCopy.singleOnly : entityCopy.history}
              aria-label={entityCopy.history}
            >
              <FiClock aria-hidden="true" />
              <span>{entityCopy.history}</span>
            </button>
            {selectedLeadIds.length ? (
              <button type="button" className="crm-leadActionClear" onClick={clearLeadSelection}>
                {pageCopy.selection.clear}
              </button>
            ) : null}
          </div>
        </section>

        {opportunityViewMode === "kanban" ? (
          <OpportunityKanban
            rows={rows}
            loading={loading}
            copy={copy}
            pageCopy={pageCopy}
            modeCopy={pipelineCopy}
            selectedPipeline={selectedOpportunityPipeline}
            pipelineFilter={opportunityPipelineFilter}
            pipelineStageIdField={pipelineStageIdField}
            pipelineRelation={pipelineRelation}
            stageOptions={stageOptions}
            saving={saving}
            onOpenDetail={openLeadDetail}
            onMoveStage={async (lead, stageId) => {
              if (!lead?.id || !stageId || saving) return;
              setSaving(true);
              setError("");
              try {
                await updateCrmLead(lead.id, { [pipelineStageIdField]: stageId });
                await loadLeads();
              } catch (err) {
                console.error("[CRM] Pipeline stage move error:", err);
                setError(err?.response?.data?.error || pageCopy.saveError);
              } finally {
                setSaving(false);
              }
            }}
            onMoveLegacyStage={async (lead, stage) => {
              if (!lead?.id || !stage || saving) return;
              setSaving(true);
              setError("");
              try {
                await updateCrmLead(lead.id, {
                  stage,
                  status: getDefaultLeadStatusForStage(stage),
                });
                await loadLeads();
              } catch (err) {
                console.error("[CRM] Pipeline legacy stage move error:", err);
                setError(err?.response?.data?.error || pageCopy.saveError);
              } finally {
                setSaving(false);
              }
            }}
          />
        ) : (
          <>
          <div className="crm-tableShell">
          <table className="crm-table">
            <thead>
              <tr>
                <th className="crm-selectCell">
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    className="crm-tableCheckbox"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    disabled={!visibleLeadIds.length}
                    aria-label={pageCopy.selection.selectVisible}
                  />
                </th>
                <th>{modeCopy?.tableTitle || pageCopy.table.title}</th>
                <th>{pipelineCopy?.pipeline || "Pipeline"}</th>
                <th>{pipelineCopy?.pipelineStage || "Pipeline stage"}</th>
                <th>{pageCopy.table.stage}</th>
                <th>{pageCopy.table.contact}</th>
                <th>{pageCopy.table.company}</th>
                <th>{pageCopy.table.owner}</th>
                <th>{pageCopy.table.value}</th>
                <th>{pageCopy.table.expectedClose}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? (
                rows.map((lead) => {
                  const stageDisplay = getLeadStageDisplay(lead, copy, pageCopy);
                  const pipelineStageDisplay = getOpportunityPipelineStageDisplay(lead, pipelineCopy, {
                    stageRelation: pipelineStageRelation,
                  });
                  const isInactiveLead = lead.status === "inactive";
                  const isSelectedLead = selectedLeadIdSet.has(lead.id);
                  const rowClassName = [
                    isInactiveLead ? "crm-tableRow--inactive" : "",
                    isSelectedLead ? "crm-tableRow--selected" : "",
                  ].filter(Boolean).join(" ");

                  return (
                    <tr key={lead.id} className={rowClassName || undefined}>
                      <td className="crm-selectCell">
                        <input
                          type="checkbox"
                          className="crm-tableCheckbox"
                          checked={isSelectedLead}
                          onChange={() => toggleLeadSelection(lead.id)}
                          aria-label={entityCopy.selectItem(lead.title)}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          className="crm-tablePrimaryButton"
                          onClick={() => openLeadDetail(lead)}
                          title={entityCopy.detailView}
                        >
                          {lead.title}
                        </button>
                        {lead.source ? <span className="crm-tableSecondary">{lead.source}</span> : null}
                      </td>
                      <td>
                        <span className={lead[pipelineIdField] ? "crm-ownerLabel" : "crm-ownerLabel is-unassigned"}>
                          {getOpportunityPipelineLabel(lead, pipelineCopy, { pipelineRelation })}
                        </span>
                      </td>
                      <td>
                        {pipelineStageDisplay ? (
                          <span
                            className={`crm-statusPill crm-statusPill--${pipelineStageDisplay.key}`}
                            style={
                              pipelineStageDisplay.color
                                ? { borderColor: pipelineStageDisplay.color, color: pipelineStageDisplay.color }
                                : undefined
                            }
                          >
                            {pipelineStageDisplay.label}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <span
                          className={`crm-statusPill crm-statusPill--${stageDisplay.key}`}
                          style={stageDisplay.color ? { borderColor: stageDisplay.color, color: stageDisplay.color } : undefined}
                        >
                          {stageDisplay.label}
                        </span>
                      </td>
                      <td>
                        {lead.contact?.id ? (
                          <button
                            type="button"
                            className="crm-inlineLinkButton"
                            onClick={() =>
                              navigate(`/crm/contacts/${lead.contact.id}`, {
                                state: buildLeadReturnState(lead),
                              })
                            }
                          >
                            {getContactLabel(lead.contact) || copy.common.noContact}
                          </button>
                        ) : (
                          getContactLabel(lead.contact) || copy.common.noContact
                        )}
                      </td>
                      <td>
                        {lead.company?.id ? (
                          <button
                            type="button"
                            className="crm-inlineLinkButton"
                            onClick={() =>
                              navigate(`/crm/companies?company_id=${lead.company.id}`, {
                                state: buildLeadReturnState(lead),
                              })
                            }
                          >
                            {lead.company.name || copy.common.noCompany}
                          </button>
                        ) : (
                          lead.company?.name || copy.common.noCompany
                        )}
                      </td>
                      <td>
                        <span className={lead.owner ? "crm-ownerLabel" : "crm-ownerLabel is-unassigned"}>
                          {getLeadOwnerLabel(lead, pageCopy)}
                        </span>
                      </td>
                      <td>{formatValue(lead.estimated_value)}</td>
                      <td>{lead.expected_close_date || "-"}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={10}>
                    <div className="crm-tableEmpty">{loading ? copy.common.loading : modeCopy?.empty || pageCopy.empty}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          <div className="crm-paginationBar">
          <span>
            {modeCopy?.paginationSummary
              ? modeCopy.paginationSummary(pageStart, pageEnd, totalRows)
              : pageCopy.pagination.summary(pageStart, pageEnd, totalRows)}
          </span>
          <div className="crm-paginationRight">
            <div className="crm-paginationControls">
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setPageIndex((prev) => Math.max(prev - 1, 0))}
                disabled={loading || !hasPreviousPage}
              >
                <FiChevronLeft aria-hidden="true" />
                <span>{pageCopy.pagination.previous}</span>
              </button>
              <span className="crm-paginationPage">{pageCopy.pagination.page(pageIndex + 1)}</span>
              <button
                type="button"
                className="crm-ghostButton"
                onClick={() => setPageIndex((prev) => prev + 1)}
                disabled={loading || !hasNextPage}
              >
                <span>{pageCopy.pagination.next}</span>
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className={`crm-ghostButton crm-inactiveToggle${showInactive ? " is-active" : ""}`}
              onClick={() => {
                setShowInactive((prev) => !prev);
                setPageIndex(0);
              }}
              disabled={loading}
            >
              {showInactive ? pageCopy.pagination.hideInactive : pageCopy.pagination.showInactive}
            </button>
          </div>
          </div>
          </>
        )}
      </section>

      <CrmMergeModal
        open={mergeModalOpen}
        copy={mergeCopy}
        items={selectedLeads}
        getItemLabel={(lead) => lead?.title || `Lead ${lead?.id || ""}`}
        saving={mergeSaving}
        error={mergeError}
        onClose={closeMergeModal}
        onSubmit={handleMergeSubmit}
      />

      {stageGuideOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <div className="crm-modal crm-stageGuideModal">
            <div className="crm-modalHeader">
              <h2>{pageCopy.stageLegendTitle}</h2>
              <button
                type="button"
                className="crm-iconButton"
                onClick={() => setStageGuideOpen(false)}
                aria-label={copy.common.cancel}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>
            <section className="crm-stageLegend crm-stageLegend--modal" aria-label={pageCopy.stageLegendTitle}>
              <div className="crm-stageLegendGrid">
                {stageOptions.map((stage) => (
                  <article key={stage.value} className="crm-stageLegendItem">
                    <span className={`crm-statusPill crm-statusPill--${stage.value}`}>{stage.label}</span>
                    <p>{pageCopy.stageLegend?.[stage.value]}</p>
                  </article>
                ))}
              </div>
            </section>
          </div>
        </div>
      ) : null}

      {detailModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <div className="crm-modal crm-leadDetailModal">
            <div className="crm-modalHeader">
              <h2>{entityCopy.detailTitle}</h2>
              <div className="crm-modalHeaderActions">
                <button
                  type="button"
                  className="crm-ghostButton"
                  onClick={openDetailEdit}
                  disabled={!detailLead || !canWorkDetailLead || detailLoading}
                >
                  <FiEdit2 aria-hidden="true" />
                  <span>{copy.common.edit}</span>
                </button>
                <button type="button" className="crm-iconButton" onClick={closeLeadDetail} aria-label={copy.common.cancel}>
                  <FiX aria-hidden="true" />
                </button>
              </div>
            </div>

            {detailError ? <div className="crm-error">{detailError}</div> : null}

            {detailLoading ? (
              <div className="crm-tableEmpty">{copy.common.loading}</div>
            ) : detailLead ? (
              <>
                <section className="crm-leadDetailHero">
                  <div>
                    <span
                      className={`crm-statusPill crm-statusPill--${detailStageDisplay?.key || "new"}`}
                      style={detailStageDisplay?.color ? { borderColor: detailStageDisplay.color, color: detailStageDisplay.color } : undefined}
                    >
                      {detailStageDisplay?.label || copy.leadStages?.new}
                    </span>
                    <h3>{detailLead.title}</h3>
                    <p>{detailLead.source || pageCopy.detail.noSource}</p>
                  </div>
                  <div className="crm-leadDetailOwner">
                    <FiUser aria-hidden="true" />
                    <span>{getLeadOwnerLabel(detailLead, pageCopy)}</span>
                  </div>
                  {detailLead.merge_info?.can_unmerge && canWorkDetailLead ? (
                    <button
                      type="button"
                      className="crm-ghostButton"
                      onClick={handleUnmergeLead}
                      disabled={unmergeSaving || detailSaving}
                    >
                      <FiGitMerge aria-hidden="true" />
                      <span>{unmergeSaving ? mergeCopy.saving : mergeCopy.unmerge}</span>
                    </button>
                  ) : null}
                </section>

                <div className="crm-leadDetailStats">
                  <div>
                    <span>{pageCopy.table.value}</span>
                    <strong>{formatValue(detailLead.estimated_value)}</strong>
                  </div>
                  <div>
                    <span>{pageCopy.table.expectedClose}</span>
                    <strong>{detailLead.expected_close_date || "-"}</strong>
                  </div>
                  <div>
                    <span>{pipelineCopy?.pipeline || "Pipeline"}</span>
                    <strong>{getOpportunityPipelineLabel(detailLead, pipelineCopy, { pipelineRelation })}</strong>
                  </div>
                  <div>
                    <span>{pipelineCopy?.pipelineStage || "Pipeline stage"}</span>
                    <strong>{detailPipelineStageDisplay?.label || "-"}</strong>
                  </div>
                  <div>
                    <span>{pageCopy.detail.updated}</span>
                    <strong>{formatDateTime(detailLead.updated_at)}</strong>
                  </div>
                </div>

                <div className="crm-leadDetailGrid">
                  <section className="crm-leadDetailPanel">
                    <div className="crm-leadDetailSectionHeader">
                      <h3>{pageCopy.detail.contacts}</h3>
                      <span>{detailContacts.length}</span>
                    </div>

                    <div className="crm-leadRelationList">
                      {detailContacts.length ? (
                        detailContacts.map((contact) => {
                          const contactLabel = getContactLabel(contact);
                          const canRemoveContact =
                            canWorkDetailLead && Number(contact.id) === Number(detailLead.contact_customer_id);

                          return (
                            <article key={contact.id} className="crm-leadRelationCard">
                              <div className="crm-leadRelationAvatar" aria-hidden="true">
                                {getEntityInitials(contactLabel)}
                              </div>
                              <div className="crm-leadRelationMain">
                                <div className="crm-leadRelationTitle">
                                  <button
                                    type="button"
                                    className="crm-inlineLinkButton"
                                    onClick={() =>
                                      navigate(`/crm/contacts/${contact.id}`, {
                                        state: buildLeadReturnState(detailLead),
                                      })
                                    }
                                  >
                                    {contactLabel}
                                  </button>
                                  {contact.is_primary_contact ? (
                                    <span>{pageCopy.detail.primaryContact}</span>
                                  ) : contact.is_company_contact ? (
                                    <span>{pageCopy.detail.companyContact}</span>
                                  ) : null}
                                </div>
                                <div className="crm-leadRelationMeta">
                                  {contact.email ? (
                                    <span>
                                      <FiMail aria-hidden="true" />
                                      {contact.email}
                                    </span>
                                  ) : null}
                                  {contact.phone ? (
                                    <span>
                                      <FiPhone aria-hidden="true" />
                                      {contact.phone}
                                    </span>
                                  ) : null}
                                  {contact.job_title ? <span>{contact.job_title}</span> : null}
                                </div>
                              </div>
                              {canRemoveContact ? (
                                <button
                                  type="button"
                                  className="crm-dangerTextButton"
                                  onClick={() => handleUnassignLeadContact(contact)}
                                  disabled={detailSaving}
                                  title={pageCopy.detail.removeContact}
                                  aria-label={pageCopy.detail.removeContact}
                                >
                                  <FiTrash2 aria-hidden="true" />
                                  <span>{pageCopy.detail.removeContact}</span>
                                </button>
                              ) : null}
                            </article>
                          );
                        })
                      ) : (
                        <div className="crm-tableEmpty">{pageCopy.detail.noContacts}</div>
                      )}
                    </div>
                  </section>

                  <section className="crm-leadDetailPanel">
                    <div className="crm-leadDetailSectionHeader">
                      <h3>{pageCopy.detail.companies}</h3>
                      <span>{detailCompanies.length}</span>
                    </div>

                    <div className="crm-leadRelationList">
                      {detailCompanies.length ? (
                        detailCompanies.map((company) => {
                          const canRemoveCompany =
                            canWorkDetailLead && Number(company.id) === Number(detailLead.company_id);

                          return (
                            <article key={company.id} className="crm-leadRelationCard">
                              <div className="crm-leadRelationAvatar crm-leadRelationAvatar--company" aria-hidden="true">
                                <FiBriefcase />
                              </div>
                              <div className="crm-leadRelationMain">
                                <div className="crm-leadRelationTitle">
                                  <button
                                    type="button"
                                    className="crm-inlineLinkButton"
                                    onClick={() =>
                                      navigate(`/crm/companies?company_id=${company.id}`, {
                                        state: buildLeadReturnState(detailLead),
                                      })
                                    }
                                  >
                                    {company.name || copy.common.noCompany}
                                  </button>
                                  <span>{pageCopy.detail.companyContacts(company.contact_count || 0)}</span>
                                </div>
                                <div className="crm-leadRelationMeta">
                                  {company.email ? (
                                    <span>
                                      <FiMail aria-hidden="true" />
                                      {company.email}
                                    </span>
                                  ) : null}
                                  {company.phone ? (
                                    <span>
                                      <FiPhone aria-hidden="true" />
                                      {company.phone}
                                    </span>
                                  ) : null}
                                  {company.city || company.country ? (
                                    <span>{[company.city, company.country].filter(Boolean).join(", ")}</span>
                                  ) : null}
                                </div>
                              </div>
                              {canRemoveCompany ? (
                                <button
                                  type="button"
                                  className="crm-dangerTextButton"
                                  onClick={() => handleUnassignLeadCompany(company)}
                                  disabled={detailSaving}
                                  title={pageCopy.detail.removeCompany}
                                  aria-label={pageCopy.detail.removeCompany}
                                >
                                  <FiTrash2 aria-hidden="true" />
                                  <span>{pageCopy.detail.removeCompany}</span>
                                </button>
                              ) : null}
                            </article>
                          );
                        })
                      ) : (
                        <div className="crm-tableEmpty">{pageCopy.detail.noCompanies}</div>
                      )}
                    </div>
                  </section>
                </div>

                {detailLead.notes ? (
                  <section className="crm-leadDetailNotes">
                    <h3>{pageCopy.fields.notes}</h3>
                    <p>{detailLead.notes}</p>
                  </section>
                ) : null}

                <CrmMergeHistory copy={mergeCopy} mergeInfo={detailLead.merge_info} />
              </>
            ) : (
              <div className="crm-tableEmpty">{entityCopy.noItem}</div>
            )}
          </div>
        </div>
      ) : null}

      {modalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleSubmit}>
            <div className="crm-modalHeader">
              <h2>
                {editingLead
                  ? modeCopy?.editTitle || pageCopy.editTitle
                  : modeCopy?.newTitle || pageCopy.newTitle}
              </h2>
              <button type="button" className="crm-iconButton" onClick={closeModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={entityCopy.fieldTitle}
                value={form.title}
                required
                onChange={(value) => handleFieldChange("title", value)}
              />
              <SelectField
                label={pageCopy.fields.stage}
                value={form.stage}
                onChange={handleLeadStageChange}
                options={stageOptions}
              />
              <SelectField
                label={pageCopy.fields.status}
                value={form.status}
                onChange={(value) => handleFieldChange("status", value)}
                options={leadStatusOptions}
              />
              <SelectField
                label={pipelineCopy?.pipeline || "Pipeline"}
                value={form[pipelineIdField]}
                onChange={handleOpportunityPipelineChange}
                options={[
                  { value: "", label: pipelineCopy?.noPipeline || "No pipeline" },
                  ...activeOpportunityPipelines.map((pipeline) => ({
                    value: pipeline.id,
                    label: `${pipeline.name}${pipeline.is_default ? " *" : ""}`,
                  })),
                ]}
              />
              <SelectField
                label={pipelineCopy?.pipelineStage || "Pipeline stage"}
                value={form[pipelineStageIdField]}
                onChange={handleOpportunityStageChange}
                options={[
                  { value: "", label: pipelineCopy?.noPipeline || "No pipeline" },
                  ...formOpportunityStageOptions.map((stage) => ({
                    value: stage.id,
                    label: stage.name,
                  })),
                ]}
                disabled={!form[pipelineIdField]}
              />
              <EntityPickerField
                label={pageCopy.fields.contact_customer_id}
                value={selectedFormContactLabel}
                empty={!form.contact_customer_id}
                icon={FiUser}
                onOpen={() => {
                  setContactPickerQuery("");
                  setContactPickerOptions(contacts);
                  setContactPickerOpen(true);
                }}
              />
              <EntityPickerField
                label={pageCopy.fields.company_id}
                value={selectedFormCompanyLabel}
                empty={!form.company_id}
                icon={FiBriefcase}
                onOpen={() => {
                  setCompanyPickerQuery("");
                  setCompanyPickerOptions(companies);
                  setCompanyPickerOpen(true);
                }}
              />
              <Field
                label={pageCopy.fields.estimated_value}
                value={form.estimated_value}
                type="number"
                step="0.01"
                onChange={(value) => handleFieldChange("estimated_value", value)}
              />
              <Field
                label={pageCopy.fields.expected_close_date}
                value={form.expected_close_date}
                type="date"
                onChange={(value) => handleFieldChange("expected_close_date", value)}
              />
              <Field
                label={pageCopy.fields.source}
                value={form.source}
                onChange={(value) => handleFieldChange("source", value)}
              />
              <Field
                label={pageCopy.fields.notes}
                value={form.notes}
                textarea
                onChange={(value) => handleFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeModal} disabled={saving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={saving}>
                {saving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {contactPickerOpen ? (
        <EntityPickerModal
          title={pageCopy.fields.contact_customer_id}
          searchLabel={copy.common.search}
          searchValue={contactPickerQuery}
          onSearchChange={setContactPickerQuery}
          newLabel={pageCopy.addContactOption}
          clearLabel={copy.common.noContact}
          emptyLabel={copy.common.noContact}
          loading={contactPickerLoading}
          loadingLabel={copy.common.loading}
          itemIcon={FiUser}
          items={filteredContactOptions}
          selectedId={form.contact_customer_id}
          getItemKey={(contact) => contact.id}
          getItemTitle={getContactLabel}
          getItemMeta={(contact) =>
            [
              contact.email,
              contact.phone,
              contact.document_number,
              contact.job_title,
              contact.department,
              contact.company?.name || contact.company?.legal_name,
            ].filter(Boolean)
          }
          onNew={() => {
            setContactPickerOpen(false);
            handleLeadContactChange(CREATE_CONTACT_VALUE);
          }}
          onClear={() => {
            handleLeadContactChange("");
            setContactPickerOpen(false);
          }}
          onSelect={(contact) => {
            handleLeadContactChange(contact.id);
            setContactPickerOpen(false);
          }}
          onClose={() => setContactPickerOpen(false)}
        />
      ) : null}

      {companyPickerOpen ? (
        <EntityPickerModal
          title={pageCopy.fields.company_id}
          searchLabel={copy.common.search}
          searchValue={companyPickerQuery}
          onSearchChange={setCompanyPickerQuery}
          newLabel={pageCopy.addCompanyOption}
          clearLabel={copy.common.noCompany}
          emptyLabel={copy.common.noCompany}
          loading={companyPickerLoading}
          loadingLabel={copy.common.loading}
          itemIcon={FiBriefcase}
          items={filteredCompanyOptions}
          selectedId={form.company_id}
          getItemKey={(company) => company.id}
          getItemTitle={getCompanyLabel}
          getItemMeta={(company) =>
            [
              company.legal_name,
              company.tax_id,
              company.website,
              company.email,
              company.phone,
              company.industry,
              [company.city, company.country].filter(Boolean).join(", "),
            ].filter(Boolean)
          }
          onNew={() => {
            setCompanyPickerOpen(false);
            handleLeadCompanyChange(CREATE_COMPANY_VALUE);
          }}
          onClear={() => {
            handleLeadCompanyChange("");
            setCompanyPickerOpen(false);
          }}
          onSelect={(company) => {
            handleLeadCompanyChange(company.id);
            setCompanyPickerOpen(false);
          }}
          onClose={() => setCompanyPickerOpen(false)}
        />
      ) : null}

      {relationshipPrompt ? (
        <div className="crm-modalBackdrop" role="presentation">
          <div className="crm-modal crm-modal--sm crm-relationshipModal">
            <div className="crm-modalHeader">
              <h2>{pageCopy.relationship?.title || "Link contact and company?"}</h2>
              <button
                type="button"
                className="crm-iconButton"
                onClick={handleCancelRelationship}
                aria-label={copy.common.cancel}
                disabled={relationshipSaving}
              >
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-relationshipSummary">
              <span className="crm-pickerOptionIcon">
                <FiUser aria-hidden="true" />
              </span>
              <strong>{getContactLabel(relationshipPrompt.contact)}</strong>
              <span className="crm-pickerOptionIcon">
                <FiBriefcase aria-hidden="true" />
              </span>
              <strong>{getCompanyLabel(relationshipPrompt.company)}</strong>
            </div>

            <p className="crm-relationshipText">
              {pageCopy.relationship?.message
                ? pageCopy.relationship.message(
                    getContactLabel(relationshipPrompt.contact),
                    getCompanyLabel(relationshipPrompt.company)
                  )
                : `${getContactLabel(relationshipPrompt.contact)} is not linked to ${getCompanyLabel(
                    relationshipPrompt.company
                  )}. Do you want to link them now?`}
            </p>
            {relationshipPrompt.currentCompany ? (
              <p className="crm-relationshipHint">
                {pageCopy.relationship?.currentCompany
                  ? pageCopy.relationship.currentCompany(relationshipPrompt.currentCompany)
                  : `Current contact company: ${relationshipPrompt.currentCompany}.`}
              </p>
            ) : null}

            <div className="crm-modalActions">
              <button
                type="button"
                className="crm-ghostButton"
                onClick={handleCancelRelationship}
                disabled={relationshipSaving}
              >
                {pageCopy.relationship?.cancel || copy.common.cancel}
              </button>
              <button
                type="button"
                className="crm-primaryButton"
                onClick={handleConfirmRelationship}
                disabled={relationshipSaving}
              >
                {relationshipSaving
                  ? pageCopy.relationship?.saving || copy.common.saving
                  : pageCopy.relationship?.confirm || "Link them"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {assignmentModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal crm-modal--sm" onSubmit={handleAssignmentSubmit}>
            <div className="crm-modalHeader">
              <h2>{assignmentModalTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeAssignmentModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={assignmentLeads.length > 1 ? entityCopy.assignmentItems : entityCopy.fieldTitle}
                value={
                  assignmentLeads.length > 1
                    ? entityCopy.selectedItems(assignmentLeads.length)
                    : assignmentLeads[0]?.title || ""
                }
                readOnly
              />
              <SelectField
                label={pageCopy.assignment.owner}
                value={assignmentOwnerId}
                onChange={setAssignmentOwnerId}
                options={[
                  { value: "", label: pageCopy.assignment.selectUser },
                  ...assignableUsers.map((user) => ({ value: user.id, label: getUserLabel(user) })),
                ]}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeAssignmentModal} disabled={assignmentSaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={assignmentSaving || !assignmentOwnerId}>
                {assignmentSaving ? copy.common.saving : pageCopy.assignment.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {historyModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <div className="crm-modal crm-modal--sm">
            <div className="crm-modalHeader">
              <h2>{entityCopy.history}</h2>
              <button type="button" className="crm-iconButton" onClick={closeHistoryModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-historyHeader">
              <strong>{historyLead?.title || entityCopy.noItem}</strong>
              <span>{pageCopy.assignment.currentOwner}: {getLeadOwnerLabel(historyLead, pageCopy)}</span>
            </div>

            <div className="crm-historyList">
              {historyLoading ? (
                <div className="crm-tableEmpty">{copy.common.loading}</div>
              ) : assignmentHistory.length ? (
                assignmentHistory.map((item) => (
                  <article key={item.id} className="crm-historyItem">
                    <div>
                      <strong>{getUserLabel(item.to_user) || pageCopy.assignment.unassigned}</strong>
                      <span>
                        {pageCopy.assignment.from}: {getUserLabel(item.from_user) || pageCopy.assignment.unassigned}
                      </span>
                    </div>
                    <div>
                      <span>{formatDateTime(item.created_at)}</span>
                      <span>
                        {pageCopy.assignment.by}: {getUserLabel(item.assigned_by) || "-"}
                      </span>
                    </div>
                  </article>
                ))
              ) : (
                <div className="crm-tableEmpty">{pageCopy.assignment.noHistory}</div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {contactModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleContactSubmit}>
            <div className="crm-modalHeader">
              <h2>{copy.entities.contacts.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeContactModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={copy.entities.contacts.fields.first_name}
                value={contactForm.first_name}
                onChange={(value) => handleContactFieldChange("first_name", value)}
              />
              <Field
                label={copy.entities.contacts.fields.last_name}
                value={contactForm.last_name}
                onChange={(value) => handleContactFieldChange("last_name", value)}
              />
              <Field
                label={copy.entities.contacts.fields.email}
                value={contactForm.email}
                type="email"
                onChange={(value) => handleContactFieldChange("email", value)}
              />
              <Field
                label={copy.entities.contacts.fields.phone}
                value={contactForm.phone}
                onChange={(value) => handleContactFieldChange("phone", value)}
              />
              <SelectField
                label={copy.entities.contacts.fields.company_id}
                value={contactForm.company_id}
                onChange={(value) => handleContactFieldChange("company_id", value)}
                options={[
                  { value: "", label: copy.common.noCompany },
                  ...companies.map((company) => ({ value: company.id, label: company.name })),
                ]}
              />
              <Field
                label={copy.entities.contacts.fields.job_title}
                value={contactForm.job_title}
                onChange={(value) => handleContactFieldChange("job_title", value)}
              />
              <Field
                label={copy.entities.contacts.fields.department}
                value={contactForm.department}
                onChange={(value) => handleContactFieldChange("department", value)}
              />
              <SelectField
                label={copy.entities.contacts.fields.crm_status}
                value={contactForm.crm_status}
                onChange={(value) => handleContactFieldChange("crm_status", value)}
                options={statusOptions}
              />
              <Field
                label={copy.entities.contacts.fields.source}
                value={contactForm.source}
                onChange={(value) => handleContactFieldChange("source", value)}
              />
              <Field
                label={copy.entities.contacts.fields.notes}
                value={contactForm.notes}
                textarea
                onChange={(value) => handleContactFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeContactModal} disabled={contactSaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={contactSaving}>
                {contactSaving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {companyModalOpen ? (
        <div className="crm-modalBackdrop" role="presentation">
          <form className="crm-modal" onSubmit={handleCompanySubmit}>
            <div className="crm-modalHeader">
              <h2>{copy.entities.companies.newTitle}</h2>
              <button type="button" className="crm-iconButton" onClick={closeCompanyModal} aria-label={copy.common.cancel}>
                <FiX aria-hidden="true" />
              </button>
            </div>

            <div className="crm-formGrid">
              <Field
                label={copy.entities.companies.fields.name}
                value={companyForm.name}
                required
                onChange={(value) => handleCompanyFieldChange("name", value)}
              />
              <Field
                label={copy.entities.companies.fields.legal_name}
                value={companyForm.legal_name}
                onChange={(value) => handleCompanyFieldChange("legal_name", value)}
              />
              <Field
                label={copy.entities.companies.fields.tax_id}
                value={companyForm.tax_id}
                onChange={(value) => handleCompanyFieldChange("tax_id", value)}
              />
              <Field
                label={copy.entities.companies.fields.website}
                value={companyForm.website}
                onChange={(value) => handleCompanyFieldChange("website", value)}
              />
              <Field
                label={copy.entities.companies.fields.email}
                value={companyForm.email}
                type="email"
                onChange={(value) => handleCompanyFieldChange("email", value)}
              />
              <Field
                label={copy.entities.companies.fields.phone}
                value={companyForm.phone}
                onChange={(value) => handleCompanyFieldChange("phone", value)}
              />
              <Field
                label={copy.entities.companies.fields.industry}
                value={companyForm.industry}
                onChange={(value) => handleCompanyFieldChange("industry", value)}
              />
              <Field
                label={copy.entities.companies.fields.company_type}
                value={companyForm.company_type}
                onChange={(value) => handleCompanyFieldChange("company_type", value)}
              />
              <SelectField
                label={copy.entities.companies.fields.status}
                value={companyForm.status}
                onChange={(value) => handleCompanyFieldChange("status", value)}
                options={statusOptions}
              />
              <Field
                label={copy.entities.companies.fields.address}
                value={companyForm.address}
                onChange={(value) => handleCompanyFieldChange("address", value)}
              />
              <Field
                label={copy.entities.companies.fields.city}
                value={companyForm.city}
                onChange={(value) => handleCompanyFieldChange("city", value)}
              />
              <Field
                label={copy.entities.companies.fields.country}
                value={companyForm.country}
                onChange={(value) => handleCompanyFieldChange("country", value)}
              />
              <Field
                label={copy.entities.companies.fields.postal_code}
                value={companyForm.postal_code}
                onChange={(value) => handleCompanyFieldChange("postal_code", value)}
              />
              <Field
                label={copy.entities.companies.fields.notes}
                value={companyForm.notes}
                textarea
                onChange={(value) => handleCompanyFieldChange("notes", value)}
              />
            </div>

            <div className="crm-modalActions">
              <button type="button" className="crm-ghostButton" onClick={closeCompanyModal} disabled={companySaving}>
                {copy.common.cancel}
              </button>
              <button type="submit" className="crm-primaryButton" disabled={companySaving}>
                {companySaving ? copy.common.saving : copy.common.save}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </main>
  );
}

function OpportunityKanban({
  rows,
  loading,
  copy,
  pageCopy,
  modeCopy,
  selectedPipeline,
  pipelineFilter,
  pipelineStageIdField = "opportunity_stage_id",
  pipelineRelation = "opportunity_pipeline",
  stageOptions,
  saving,
  onOpenDetail,
  onMoveStage,
  onMoveLegacyStage,
}) {
  const [draggedLeadId, setDraggedLeadId] = useState(null);
  const usesPipelineStages = Boolean(selectedPipeline?.id && pipelineFilter !== NO_PIPELINE_FILTER);
  const columns = useMemo(() => {
    if (usesPipelineStages) {
      return (selectedPipeline?.stages || [])
        .filter((stage) => stage.status !== "inactive")
        .map((stage) => ({
          key: `pipeline-${stage.id}`,
          id: stage.id,
          label: stage.name,
          color: stage.color,
          type: stage.stage_type || "open",
        }));
    }

    return stageOptions.map((stage) => ({
      key: `legacy-${stage.value}`,
      id: stage.value,
      label: stage.label,
      type: stage.value,
    }));
  }, [selectedPipeline, stageOptions, usesPipelineStages]);

  const rowsById = useMemo(() => new Map(rows.map((lead) => [String(lead.id), lead])), [rows]);
  const getLeadColumnValue = (lead) =>
    usesPipelineStages ? String(lead[pipelineStageIdField] || "") : String(lead.stage || "");
  const getColumnRows = (column) =>
    rows.filter((lead) => getLeadColumnValue(lead) === String(column.id));

  const handleMoveSelect = (lead, nextColumnId) => {
    if (!lead || !nextColumnId || saving || !lead.permissions?.can_work) return;
    if (getLeadColumnValue(lead) === String(nextColumnId)) return;
    if (usesPipelineStages) {
      onMoveStage(lead, nextColumnId);
      return;
    }
    onMoveLegacyStage(lead, nextColumnId);
  };

  const handleDrop = (event, column) => {
    event.preventDefault();
    const lead = rowsById.get(String(draggedLeadId || event.dataTransfer.getData("text/plain")));
    setDraggedLeadId(null);
    if (!lead || saving || !lead.permissions?.can_work) return;
    if (usesPipelineStages) {
      if (String(lead[pipelineStageIdField] || "") === String(column.id)) return;
      onMoveStage(lead, column.id);
      return;
    }
    if (String(lead.stage || "") === String(column.id)) return;
    onMoveLegacyStage(lead, column.id);
  };

  if (!columns.length) {
    return <div className="crm-kanbanEmpty">{loading ? copy.common.loading : modeCopy?.pipelineEmpty || "No stages"}</div>;
  }

  return (
    <div className="crm-opportunityKanban" aria-busy={loading}>
      {columns.map((column) => {
        const columnRows = getColumnRows(column);
        const columnValue = columnRows.reduce((sum, lead) => sum + Number(lead.estimated_value || 0), 0);
        return (
          <section
            key={column.key}
            className="crm-kanbanColumn"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => handleDrop(event, column)}
          >
            <header className="crm-kanbanColumnHeader" style={column.color ? { borderColor: column.color } : undefined}>
              <div>
                <strong>{column.label}</strong>
                <span>
                  {modeCopy?.itemCount
                    ? modeCopy.itemCount(columnRows.length)
                    : `${columnRows.length} opportunities`}
                </span>
              </div>
              <em>{formatValue(columnValue)}</em>
            </header>
            <div className="crm-kanbanColumnBody">
              {columnRows.length ? (
                columnRows.map((lead) => {
                  const canMove = Boolean(lead.permissions?.can_work) && !saving && columns.length > 1;
                  const currentColumnValue = getLeadColumnValue(lead);

                  return (
                    <article
                      key={lead.id}
                      className={`crm-kanbanCard crm-kanbanCard--${lead.status || "open"}`}
                      draggable={Boolean(lead.permissions?.can_work) && !saving}
                      onDragStart={(event) => {
                        setDraggedLeadId(lead.id);
                        event.dataTransfer.setData("text/plain", String(lead.id));
                      }}
                        onDragEnd={() => setDraggedLeadId(null)}
                    >
                      <button type="button" onClick={() => onOpenDetail(lead)}>
                        <strong>{lead.title}</strong>
                        <span>{getOpportunityPipelineLabel(lead, modeCopy, { pipelineRelation })}</span>
                        {lead.company?.name ? <span>{lead.company.name}</span> : null}
                        {lead.contact ? <span>{getContactLabel(lead.contact)}</span> : null}
                      </button>
                      <footer>
                        <span>{getLeadOwnerLabel(lead, pageCopy)}</span>
                        <em>{formatValue(lead.estimated_value)}</em>
                      </footer>
                      <label
                        className="crm-kanbanMoveControl"
                        onClick={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                      >
                        <span>{modeCopy?.moveToStage || "Move to stage"}</span>
                        <select
                          value={currentColumnValue}
                          disabled={!canMove}
                          onChange={(event) => handleMoveSelect(lead, event.target.value)}
                        >
                          {columns.map((option) => (
                            <option key={option.key} value={option.id}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </article>
                  );
                })
              ) : (
                <div className="crm-kanbanDropHint">
                  {loading ? copy.common.loading : modeCopy?.noItemsInStage || modeCopy?.noOpportunitiesInStage || "No items here"}
                </div>
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  textarea = false,
  required = false,
  step,
  readOnly = false,
}) {
  return (
    <label className={`crm-field ${textarea ? "crm-field--wide" : ""}`}>
      <span>{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          rows={4}
          required={required}
          readOnly={readOnly}
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          required={required}
          step={step}
          readOnly={readOnly}
        />
      )}
    </label>
  );
}

function EntityPickerField({ label, value, empty = false, icon: Icon, onOpen }) {
  return (
    <label className="crm-field crm-pickerField">
      <span>{label}</span>
      <button type="button" className="crm-pickerFieldButton" onClick={onOpen}>
        {Icon ? <Icon aria-hidden="true" /> : null}
        <strong className={empty ? "crm-pickerFieldValue crm-pickerFieldValue--empty" : "crm-pickerFieldValue"}>
          {value}
        </strong>
        <FiChevronDown aria-hidden="true" />
      </button>
    </label>
  );
}

function EntityPickerModal({
  title,
  searchLabel,
  searchValue,
  onSearchChange,
  newLabel,
  clearLabel,
  emptyLabel,
  loading = false,
  loadingLabel,
  itemIcon: ItemIcon,
  items,
  selectedId,
  getItemKey,
  getItemTitle,
  getItemMeta,
  onNew,
  onClear,
  onSelect,
  onClose,
}) {
  const newOptionLabel = String(newLabel || "").replace(/^\+\s*/, "");

  return (
    <div className="crm-modalBackdrop crm-pickerBackdrop" role="presentation">
      <div className="crm-modal crm-modal--picker">
        <div className="crm-modalHeader">
          <h2>{title}</h2>
          <button type="button" className="crm-iconButton" onClick={onClose} aria-label={title}>
            <FiX aria-hidden="true" />
          </button>
        </div>

        <button type="button" className="crm-pickerNewOption" onClick={onNew}>
          <span className="crm-pickerOptionIcon">
            <FiPlus aria-hidden="true" />
          </span>
          <strong>{newOptionLabel}</strong>
        </button>

        <label className="crm-searchBox crm-pickerSearch" aria-label={searchLabel}>
          <FiSearch aria-hidden="true" />
          <input
            type="search"
            value={searchValue}
            placeholder={searchLabel}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </label>

        <button
          type="button"
          className={!selectedId ? "crm-pickerClearOption crm-pickerClearOption--selected" : "crm-pickerClearOption"}
          onClick={onClear}
        >
          {clearLabel}
        </button>

        <div className="crm-pickerList">
          {loading && !items.length ? (
            <div className="crm-tableEmpty">{loadingLabel}</div>
          ) : items.length ? (
            items.map((item) => {
              const itemId = getItemKey(item);
              const selected = String(itemId) === String(selectedId);
              const meta = getItemMeta?.(item) || [];

              return (
                <button
                  key={itemId}
                  type="button"
                  className={selected ? "crm-pickerOption crm-pickerOption--selected" : "crm-pickerOption"}
                  onClick={() => onSelect(item)}
                >
                  <span className="crm-pickerOptionIcon">
                    {ItemIcon ? <ItemIcon aria-hidden="true" /> : null}
                  </span>
                  <span className="crm-pickerOptionMain">
                    <strong>{getItemTitle(item)}</strong>
                    {meta.length ? <span>{meta.join(" | ")}</span> : null}
                  </span>
                </button>
              );
            })
          ) : (
            <div className="crm-tableEmpty">{emptyLabel}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled = false }) {
  return (
    <label className="crm-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
